import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { coursesService, API_ORIGIN, audiosService, paymentsService } from '../services/api';
import type {
  Course,
  CourseLessonUpdatePublic,
  CoursePracticeSessionPublic,
  CourseSatisfactionKpiPublic,
  CourseSatisfactionStoryPublic,
} from '../types';
import { aparatShareToEmbedUrl } from '../utils/aparatEmbed';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { getImageUrlWithFallback } from '../utils/imageUtils';
import { getCoursePathSegment } from '../utils/coursePath';
import SiteBrandedLoader, { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import { FreeBadge } from '../components/FreeBadge';
import { CourseTemplateComments } from '../components/courses/CourseTemplateComments';
import { CourseVideoPlayer } from '../components/courses/CourseVideoPlayer';
import CourseAudioPlayer from '../components/audio/CourseAudioPlayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faUsers,
  faClock,
  faRotate,
  faInfinity,
  faDisplay,
  faArrowsRotate,
  faComments,
  faCircleCheck,
  faPlay,
  faXmark,
  faChevronRight,
  faChevronLeft,
  faChevronDown,
  faFilePdf,
  faDownload,
  faArrowUpRightFromSquare,
  faClipboardCheck,
  faThumbsUp,
  faThumbsDown,
  faTag,
  faGaugeHigh,
  faListCheck,
  faCirclePlay,
  faHeadphonesSimple,
  faCartShopping,
  faBookmark,
  faLock,
  faShareNodes,
  faLink,
  faEnvelope,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram, faWhatsapp, faXTwitter, faLinkedin, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import './course-detail-touch.css';

const DEMO_COURSE_ID = 'demo-course';

const isMobileCourseLayout = () =>
  typeof window !== 'undefined' &&
  (window.innerWidth < 1024 || window.matchMedia('(pointer: coarse)').matches);

const DEMO_SATISFACTION_STORIES_FALLBACK: CourseSatisfactionStoryPublic[] = [
  {
    id: 'sat-1',
    name: 'الهام مرادی',
    role: 'کارآفرین',
    score: 5,
    comment:
      'ساختار دوره خیلی کاربردی بود. بعد از یک ماه، برنامه روزانه‌ام منظم‌تر و خروجی کارم کاملا بهتر شد.',
    cover: '/assets/homeV2/01.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
  {
    id: 'sat-2',
    name: 'رضا امینی',
    role: 'مدیر محصول',
    score: 5,
    comment:
      'تمرین‌ها فقط تئوری نبودند و دقیقا قابل اجرا بودند. از نظر کیفیت محتوا یکی از بهترین دوره‌هایی بود که دیدم.',
    cover: '/assets/homeV2/Drifter.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
  {
    id: 'sat-3',
    name: 'نیلوفر شریفی',
    role: 'مدرس',
    score: 4,
    comment:
      'از زبان ساده و مسیر مرحله‌به‌مرحله دوره خیلی راضی بودم. مخصوصا بخش تصمیم‌گیری در شرایط پیچیده عالی بود.',
    cover: '/assets/homeV2/Artboard 2 copy 4.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
  {
    id: 'sat-4',
    name: 'محمد نیک‌فر',
    role: 'تحلیلگر کسب‌وکار',
    score: 5,
    comment: 'یکی از بهترین بخش‌ها برای من، تمرین‌های کوتاه روزانه بود. هم ساده بود هم اثرش سریع دیده شد.',
    cover: '/assets/homeV2/Emotional Podcast2.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
  {
    id: 'sat-5',
    name: 'سحر محمودی',
    role: 'فریلنسر',
    score: 4,
    comment: 'برای نظم ذهنی و مدیریت زمان خیلی به من کمک کرد. از ساختار و زبان آموزش کاملا راضی بودم.',
    cover: '/assets/homeV2/Sexual Energy Transmutation.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
  {
    id: 'sat-6',
    name: 'آرمان صادقی',
    role: 'برنامه‌نویس',
    score: 5,
    comment: 'محتوا کاملا کاربردی و قابل اجرا بود. مهم‌تر از همه این‌که حس پیشرفت واقعی در کار و زندگی داشتم.',
    cover: '/assets/homeV2/Drifter.jpg',
    videoEmbedUrl: 'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame',
  },
];

const DEMO_SATISFACTION_KPIS_FALLBACK: CourseSatisfactionKpiPublic[] = [
  { label: 'رضایت کلی', valueFa: '۹۴٪' },
  { label: 'کیفیت محتوا', valueFa: '۹۱٪' },
  { label: 'اثرگذاری عملی', valueFa: '۸۹٪' },
  { label: 'پیشنهاد به دیگران', valueFa: '۹۶٪' },
];

const DEMO_SATISFACTION_AVG_FALLBACK = '۴٫۹/۵';

const FAQ_TAB_LABELS: Record<'learning' | 'access' | 'payment', string> = {
  learning: 'فرآیند یادگیری',
  access: 'دسترسی و محتوا',
  payment: 'پرداخت و پشتیبانی',
};
/** پیش‌فرض برای دکمهٔ معرفی و کارت‌های دمو وقتی لینک اختصاصی نیست */
const FALLBACK_APARAT_DEMO_EMBED =
  'https://www.aparat.com/video/video/embed/videohash/NfR6a/vt/frame';

const DEMO_PRACTICE_PDF_URL =
  'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';

/** نمایشی برای تب «تمرین‌ها و جزوات» دورهٔ دمو (بدون نیاز به بک‌اند) */
const DEMO_PRACTICE_SESSIONS: CoursePracticeSessionPublic[] = [
  {
    id: 'practice-1',
    title: 'تمرین عملی جلسه ۱: طراحی نقشه تمرکز روزانه',
    sortOrder: 0,
    pamphletUrl: DEMO_PRACTICE_PDF_URL,
    tasks: [
      { id: 'demo-task-1-1', sortOrder: 0, text: 'چک‌لیست عوامل حواس‌پرتی خود را کامل کنید.' },
      { id: 'demo-task-1-2', sortOrder: 1, text: '۳ بازه ۲۵ دقیقه‌ای تمرکز عمیق را در تقویم ثبت کنید.' },
      { id: 'demo-task-1-3', sortOrder: 2, text: 'نتیجه اجرای روز اول را در فرم بازخورد بنویسید.' },
    ],
  },
  {
    id: 'practice-2',
    title: 'تمرین عملی جلسه ۲: بازطراحی عادت‌های ناکارآمد',
    sortOrder: 1,
    pamphletUrl: DEMO_PRACTICE_PDF_URL,
    tasks: [
      {
        id: 'demo-task-2-1',
        sortOrder: 0,
        text: 'یک عادت بازدارنده را شناسایی و تحلیل محرک-رفتار-نتیجه انجام دهید.',
      },
      { id: 'demo-task-2-2', sortOrder: 1, text: 'جایگزین رفتاری پیشنهادی را برای ۳ روز اجرا کنید.' },
      { id: 'demo-task-2-3', sortOrder: 2, text: 'موانع اجرا و راهکارها را به‌صورت مکتوب ثبت کنید.' },
    ],
  },
  {
    id: 'practice-3',
    title: 'تمرین عملی جلسه ۳: مدل تصمیم‌گیری سه‌مرحله‌ای',
    sortOrder: 2,
    pamphletUrl: DEMO_PRACTICE_PDF_URL,
    tasks: [
      { id: 'demo-task-3-1', sortOrder: 0, text: 'یک تصمیم واقعی این هفته را با مدل سه‌مرحله‌ای تحلیل کنید.' },
      { id: 'demo-task-3-2', sortOrder: 1, text: 'برای هر گزینه، ریسک/ارزش را در جدول ارزیابی کنید.' },
      { id: 'demo-task-3-3', sortOrder: 2, text: 'تصمیم نهایی و معیار بازبینی آن را مشخص کنید.' },
    ],
  },
];

function lessonUpdateBadgeLabel(b: CourseLessonUpdatePublic['badge']): string {
  return b === 'NEW' ? 'جدید' : b === 'IMPROVEMENT' ? 'بهبود' : 'اطلاعیه';
}

function lessonUpdateKindLabel(k: CourseLessonUpdatePublic['contentKind']): string {
  return k === 'VIDEO' ? 'ویدیو' : k === 'AUDIO' ? 'صوت' : 'ویدیو + صوت';
}

type LessonUpdateDisplayRow = {
  id: string;
  title: string;
  notes: string;
  status: string;
  kind: string;
  duration: string;
  date: string;
  version: string;
  apiKind: CourseLessonUpdatePublic['contentKind'];
  videoIframeSrc: string | null;
  videoFileSrc: string | null;
  audioSrc: string | null;
};

type DemoComment = {
  id: string;
  author: string;
  role: string;
  content: string;
  rating: number;
  createdAt: string;
  helpfulCount: number;
  likes?: number;
  dislikes?: number;
  hasInstructorReply?: boolean;
  instructorReplyText?: string;
  instructorReplyAuthor?: string;
  instructorReplyAvatar?: string;
};

const demoCourse: Course = {
  id: DEMO_COURSE_ID,
  slug: DEMO_COURSE_ID,
  title: 'استاد درون - دوره جامع بازطراحی ذهن',
  description:
    'این یک دیتای تستی برای نمایش قالب صفحه دوره است. در نسخه نهایی، این بخش با داده‌های واقعی API داینامیک می‌شود. در این دوره روی خودشناسی، عادت‌های ذهنی و ساخت سیستم رشد شخصی کار می‌کنیم.',
  price: 2900000,
  listPrice: 3920000,
  hasActiveDiscount: true,
  discountType: 'PERCENT',
  discountPercent: 26,
  discountEndsAt: null,
  thumbnail: '/assets/faraz.jpg',
  published: true,
  allowComments: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  videos: [
    {
      id: 'demo-video-1',
      title: 'جلسه ۱: نقشه راه دوره',
      description: 'مرور ساختار دوره و مسیر اجرایی ۹۰ روزه.',
      videoFile: 'demo-video-1.mp4',
      thumbnail: '/assets/homeV2/Emotional Podcast2.jpg',
      duration: 1240,
      order: 1,
      courseId: DEMO_COURSE_ID,
      published: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'demo-video-2',
      title: 'جلسه ۲: شکستن الگوهای محدودکننده',
      description: 'تمرین‌های عملی برای تغییر باورهای فرساینده.',
      videoFile: 'demo-video-2.mp4',
      thumbnail: '/assets/homeV2/Sexual Energy Transmutation.jpg',
      duration: 1860,
      order: 2,
      courseId: DEMO_COURSE_ID,
      published: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  audios: [],
  attachments: [
    '/assets/homeV2/Artboard 2 copy 4.jpg',
    '/assets/homeV2/Drifter.jpg',
  ],
  faqs: [
    {
      id: 'faq-demo-1',
      category: 'learning',
      sortOrder: 0,
      question: 'این دوره مناسب چه افرادی است؟',
      answer:
        'برای افرادی که می‌خواهند مسیر رشد شخصی خود را ساختاریافته و نتیجه‌محور پیش ببرند. اگر دنبال صرفاً محتوای تئوری نیستید و اجرای واقعی می‌خواهید، این دوره مناسب شماست.',
    },
    {
      id: 'faq-demo-2',
      category: 'access',
      sortOrder: 1,
      question: 'آیا دسترسی دوره محدود زمانی دارد؟',
      answer:
        'خیر، پس از ثبت‌نام دسترسی شما مادام‌العمر است و از طریق موبایل و دسکتاپ می‌توانید تمام جلسات را مشاهده کنید.',
    },
    {
      id: 'faq-demo-3',
      category: 'payment',
      sortOrder: 2,
      question: 'اگر بعد از پرداخت مشکل فنی پیش آمد چه کاری انجام دهم؟',
      answer:
        'از طریق پنل کاربری و بخش پشتیبانی، شماره تراکنش خود را ارسال کنید. تیم پشتیبانی در سریع‌ترین زمان بررسی و پیگیری می‌کند.',
    },
    {
      id: 'faq-demo-4',
      category: 'learning',
      sortOrder: 3,
      question: 'برای نتیجه بهتر، روزانه چقدر زمان باید بگذارم؟',
      answer:
        'پیشنهاد ما روزانه ۳۰ تا ۴۵ دقیقه برای مشاهده محتوا و اجرای تمرین‌ها است. استمرار مهم‌تر از حجم مطالعه است.',
    },
    {
      id: 'faq-demo-5',
      category: 'access',
      sortOrder: 4,
      question: 'آیا فایل‌های دوره قابل دانلود هستند؟',
      answer:
        'ویدیوها به صورت استریم امن پخش می‌شوند. در صورت وجود فایل‌های ضمیمه، لینک دانلود آن‌ها داخل صفحه دوره در دسترس خواهد بود.',
    },
  ],
};

/** فقط برای دمو / fallback وقتی API خالی است */
const DEFAULT_LEARNING_POINTS = [
  'طراحی نقشه رشد شخصی و حرفه‌ای برای ۹۰ روز آینده',
  'شناسایی الگوهای ذهنی بازدارنده و جایگزینی آن‌ها',
  'افزایش تمرکز، ثبات رفتاری و پیگیری عادت‌های کلیدی',
  'ساخت سیستم تصمیم‌گیری دقیق در شرایط پیچیده',
];
const DEFAULT_SUMMARY_BULLETS = [
  'مناسب برای علاقه‌مندان به رشد فردی، کارآفرینان و مدیران',
  'مسیر مرحله‌به‌مرحله همراه با تمرین عملی هر جلسه',
  'تمرکز روی نتیجه واقعی، نه صرفا محتوای تئوری',
];
const DEFAULT_LONG_COURSE_ABOUT = `
اگر مدتی است احساس می‌کنید با وجود تلاش زیاد، کیفیت خروجی ذهنی و رفتاری‌تان در سطحی که می‌خواهید نیست، این دوره دقیقاً برای همین نقطه طراحی شده است. «استاد درون» یک برنامه آموزشی صرف نیست؛ یک نقشه مهندسی‌شده برای بازطراحی الگوهای ذهنی، تصمیم‌گیری و اجرای روزانه است تا از چرخه انگیزه‌های کوتاه‌مدت، افت‌های تکرارشونده و شروع‌های بی‌نتیجه خارج شوید.

در این مسیر، ابتدا معماری ذهنی شما بازبینی می‌شود: باورهای محدودکننده، عادت‌های پنهان، محرک‌های احساسی و نقاط اصطکاکی که باعث تعویق یا فرسودگی می‌شوند. سپس با یک مدل مرحله‌ای، یاد می‌گیرید چگونه تمرکز عمیق بسازید، انرژی روانی را مدیریت کنید و در شرایط پیچیده، تصمیم‌های دقیق و قابل دفاع بگیرید. این آموزش به‌جای توصیه‌های کلی، روی تکنیک‌های قابل اجرا و سناریوهای واقعی زندگی و کار تمرکز دارد.

ساختار دوره به‌صورت «یادگیری + اجرا + بازخورد» طراحی شده است؛ یعنی بعد از هر بخش، تمرین‌هایی دارید که مستقیماً روی رفتار روزانه شما اثر می‌گذارند. خروجی این مدل، تبدیل آگاهی به عملکرد است: از دانستنِ چه باید کرد، به توانایی انجامِ پایدار آن. به همین دلیل، حتی اگر زمان محدودی داشته باشید، می‌توانید با یک چارچوب سبک اما منظم، روند رشد خودتان را بدون فشار فرساینده حفظ کنید.

در نهایت، هدف دوره صرفاً بهتر شدن مقطعی نیست؛ هدف، ساخت یک سیستم شخصیِ رشد است که در حوزه‌های مختلف زندگی‌تان پاسخ بدهد: مدیریت پروژه‌های فردی، کیفیت ارتباطات، ثبات رفتاری، و حس تسلط در مواجهه با چالش‌های روزمره. اگر به‌دنبال یک تغییر عمیق، حرفه‌ای و ماندگار هستید، این دوره می‌تواند نقطه شروع جدی شما باشد.
`.trim();

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isDemoCourse = id === DEMO_COURSE_ID;
  const location = useLocation();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, hasItem } = useCart();
  const demoViewMode = useMemo<'buyer' | 'guest'>(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('demo');
    return mode === 'guest' ? 'guest' : 'buyer';
  }, [location.search]);
  /** مثلاً ?coupon=SAVE10 — هنگام خرید مستقیم از صفحهٔ دوره به بک‌اند ارسال می‌شود */
  const checkoutCouponFromQuery = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('coupon')?.trim();
    return raw ? raw.toUpperCase() : undefined;
  }, [location.search]);
  const isDemoGuest = isDemoCourse && demoViewMode === 'guest';

  /** پخش جلسات: دمو فقط در حالت buyer؛ دورهٔ واقعی فقط با لاگین + ثبت‌نام */
  const canConsumeLessonMedia = useMemo(
    () => (isDemoCourse ? isEnrolled : Boolean(user && isEnrolled)),
    [isDemoCourse, isEnrolled, user],
  );

  const [activeTab, setActiveTab] = useState<'overview' | 'prerequisites' | 'curriculum' | 'updates' | 'practice' | 'comments' | 'faq'>('overview');
  const [expandedTrack, setExpandedTrack] = useState<string | number>(0);
  const [demoDiscountEndsAtMs] = useState(() => Date.now() + 36 * 60 * 60 * 1000);
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [commentQuery, setCommentQuery] = useState('');
  const [commentSort, setCommentSort] = useState<'newest' | 'topRated' | 'mostHelpful'>('newest');
  const [commentRateFilter, setCommentRateFilter] = useState<number | 'all'>('all');
  const [commentRoleFilter, setCommentRoleFilter] = useState<'all' | 'participant' | 'manager' | 'entrepreneur'>('all');
  const [commentWithReplyOnly, setCommentWithReplyOnly] = useState(false);
  const [commentHelpfulOnly, setCommentHelpfulOnly] = useState(false);
  const [commentEngagementFilter, setCommentEngagementFilter] = useState<'all' | 'liked' | 'disliked'>('all');
  const [showFullHeroDescription, setShowFullHeroDescription] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentRating, setNewCommentRating] = useState(5);
  const [faqQuery, setFaqQuery] = useState('');
  const [faqCategory, setFaqCategory] = useState<'all' | 'access' | 'learning' | 'payment'>('all');
  const [expandedFaqId, setExpandedFaqId] = useState<string>('faq-1');
  const [faqQuestionDraft, setFaqQuestionDraft] = useState('');
  const [showCourseProgressDetails, setShowCourseProgressDetails] = useState(false);
  const [isIntroVideoModalOpen, setIsIntroVideoModalOpen] = useState(false);
  const [lessonMediaModal, setLessonMediaModal] = useState<
    null | { title: string; variant: 'iframe' | 'video' | 'audio'; src: string }
  >(null);
  const [isSatisfactionVideoModalOpen, setIsSatisfactionVideoModalOpen] = useState(false);
  const [activeSatisfactionVideoUrl, setActiveSatisfactionVideoUrl] = useState('');
  const [activeSatisfactionVideoTitle, setActiveSatisfactionVideoTitle] = useState('');
  const [activePracticeSessionId, setActivePracticeSessionId] = useState('');
  const [isPracticePdfModalOpen, setIsPracticePdfModalOpen] = useState(false);
  /** جزوه از API روی دامنهٔ دیگری سرو می‌شود؛ blob می‌سازیم تا هدرهای امنیتی iframe را بلاک نکنند */
  const [practicePdfBlobUrl, setPracticePdfBlobUrl] = useState('');
  const [practicePdfLoading, setPracticePdfLoading] = useState(false);
  const [practicePdfFailed, setPracticePdfFailed] = useState(false);
  /** تکمیل آیتم‌های چک‌لیست؛ کلید = taskId از API یا دمو */
  const [completedPracticeTasks, setCompletedPracticeTasks] = useState<Record<string, boolean>>({});
  const [practiceFeedbacks, setPracticeFeedbacks] = useState<
    Record<string, { text: string; rating: number; submitted: boolean }>
  >({});
  const satisfactionCarouselRef = useRef<HTMLDivElement>(null);
  const articlesCarouselRef = useRef<HTMLDivElement>(null);
  const prerequisitesCarouselRef = useRef<HTMLDivElement>(null);
  const tabsBarRef = useRef<HTMLDivElement>(null);
  const tabsGridContainerRef = useRef<HTMLDivElement>(null);
  const tabContentStartRef = useRef<HTMLDivElement>(null);
  const commentsListRef = useRef<HTMLDivElement>(null);
  const hasMountedTabChangeRef = useRef(false);
  const hasMountedCommentFilterRef = useRef(false);
  const [isTabsDockedToSidebar, setIsTabsDockedToSidebar] = useState(false);
  const [tabsDockOpacity, setTabsDockOpacity] = useState(0);
  const [stickyHeaderTop, setStickyHeaderTop] = useState(96);

  const readStickyHeaderHeight = useCallback(() => {
    if (typeof window === 'undefined') return 96;
    const css = getComputedStyle(document.documentElement)
      .getPropertyValue('--site-header-height')
      .trim();
    const parsed = Number.parseInt(css.replace('px', ''), 10);
    return Number.isFinite(parsed) ? Math.max(64, parsed) : 96;
  }, []);

  const tabsStickyTop = Math.max(56, stickyHeaderTop - 4);
  const sidebarDockTop = stickyHeaderTop + 24;
  const asideStickyTop = stickyHeaderTop + 12;

  const [hasPlayedTabsDockIntro, setHasPlayedTabsDockIntro] = useState(false);
  const [isTabsDockIntroAnimating, setIsTabsDockIntroAnimating] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showCartToast, setShowCartToast] = useState(false);
  const [cartToastThumb, setCartToastThumb] = useState('/assets/homeV2/01.jpg');
  const cartToastTimerRef = useRef<number | null>(null);
  const [demoComments, setDemoComments] = useState<DemoComment[]>([
    {
      id: 'demo-c-1',
      author: 'سارا امیری',
      role: 'شرکت‌کننده دوره',
      content:
        'از ساختار مرحله‌به‌مرحله دوره خیلی راضی بودم. تمرین‌ها کاربردی بود و باعث شد برنامه روزانه‌ام منظم‌تر بشه.',
      rating: 5,
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 18,
      likes: 21,
      dislikes: 2,
      hasInstructorReply: true,
      instructorReplyAuthor: 'تیم آموزشی',
      instructorReplyText: 'خوشحالیم که مسیر دوره براتون کاربردی بوده. لطفا نتایج تمرین هفته دوم رو هم در فرم بازخورد ثبت کنید.',
      instructorReplyAvatar: '/assets/homeV2/01.jpg',
    },
    {
      id: 'demo-c-2',
      author: 'محمدرضا کاظمی',
      role: 'مدیر محصول',
      content:
        'کیفیت توضیحات بالا بود و مهم‌تر از همه، تکنیک‌ها قابل اجرا بودن. بعد از دو هفته تاثیرش رو تو تمرکزم حس کردم.',
      rating: 4,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 12,
      likes: 14,
      dislikes: 1,
    },
    {
      id: 'demo-c-3',
      author: 'الناز رستمی',
      role: 'کارآفرین',
      content:
        'بخش بازطراحی عادت‌ها فوق‌العاده بود. اگر مثال‌های بیشتری از کیس‌های واقعی اضافه شود عالی‌تر هم می‌شود.',
      rating: 4,
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 9,
      likes: 9,
      dislikes: 3,
    },
    {
      id: 'demo-c-4',
      author: 'مهدی تقوی',
      role: 'شرکت‌کننده دوره',
      content:
        'من با اجرای تمرین روتین صبحگاهی واقعا نتیجه گرفتم. پیشنهاد می‌کنم چک‌لیست هفتگی هم اضافه شود.',
      rating: 5,
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 24,
      likes: 28,
      dislikes: 1,
      hasInstructorReply: true,
      instructorReplyAuthor: 'استاد دوره',
      instructorReplyText: 'بازخورد بسیار خوبی بود. چک‌لیست هفتگی در آپدیت بعدی اضافه می‌شود.',
      instructorReplyAvatar: '/assets/homeV2/Drifter.jpg',
    },
    {
      id: 'demo-c-5',
      author: 'نرگس حسینی',
      role: 'مدیر منابع انسانی',
      content:
        'کیفیت دوره خوب بود اما ریتم بعضی جلسات برای من کمی کند بود. اگر نسخه خلاصه هم باشد عالی می‌شود.',
      rating: 3,
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 7,
      likes: 4,
      dislikes: 6,
    },
    {
      id: 'demo-c-6',
      author: 'حمید شفیعی',
      role: 'کارآفرین',
      content:
        'در مقایسه با دوره‌های مشابه، این دوره مثال‌های عملی‌تری داشت. مخصوصا بخش مدیریت عادت خیلی کاربردی بود.',
      rating: 4,
      createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 15,
      likes: 16,
      dislikes: 2,
    },
    {
      id: 'demo-c-7',
      author: 'الهام صادقی',
      role: 'شرکت‌کننده دوره',
      content:
        'به نظرم کیفیت محتوا خوبه ولی فایل‌های جزوه بعضی جلسه‌ها دیر منتشر می‌شن. لطفا زمان‌بندی دقیق اعلام کنید.',
      rating: 2,
      createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 5,
      likes: 2,
      dislikes: 8,
      hasInstructorReply: true,
      instructorReplyAuthor: 'پشتیبانی آموزشی',
      instructorReplyText: 'حق با شماست. از این هفته انتشار جزوات حداکثر ۲۴ ساعت بعد از هر جلسه انجام می‌شود.',
      instructorReplyAvatar: '/assets/faraz.jpg',
    },
    {
      id: 'demo-c-8',
      author: 'رضا نعمتی',
      role: 'مدیر محصول',
      content:
        'برای من که شاغل هستم، ساختار کوتاه و مرحله‌ای خیلی مناسب بود. مرورهای انتهای هر فصل نقطه قوت دوره است.',
      rating: 5,
      createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      helpfulCount: 19,
      likes: 22,
      dislikes: 0,
    },
  ]);

  const fetchCourse = async () => {
    if (isDemoCourse) {
      setCourse(demoCourse);
      setIsEnrolled(!isDemoGuest);
      setError('');
      setLoading(false);
      return;
    }

    try {
      const data = await coursesService.getById(id!);
      setCourse(data);
      
      // Check if user is enrolled
      if (user) {
        const myCourses = await coursesService.getMyCourses();
        const enrolled = myCourses.some(c => c.id === data.id);
        setIsEnrolled(enrolled);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت دوره');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      void fetchCourse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCourse باید user فعلی را ببندد
  }, [id, isDemoGuest, user?.id]);

  useEffect(() => {
    if (!canConsumeLessonMedia) {
      setExpandedTrack(-1);
    }
  }, [canConsumeLessonMedia]);

  useEffect(() => {
    setCompletedPracticeTasks({});
    setPracticeFeedbacks({});
  }, [id]);

  useEffect(() => {
    const apiCourseId = course?.id;
    if (!apiCourseId || isDemoCourse || !user?.id || !isEnrolled) return;
    let cancelled = false;
    void coursesService
      .getPracticeState(apiCourseId)
      .then((data) => {
        if (cancelled) return;
        const ct: Record<string, boolean> = {};
        (data.completedTaskIds || []).forEach((tid) => {
          ct[tid] = true;
        });
        setCompletedPracticeTasks(ct);
        const fb: Record<string, { text: string; rating: number; submitted: boolean }> = {};
        (data.feedbacks || []).forEach((f) => {
          fb[f.practiceSessionId] = {
            rating: Math.min(5, Math.max(1, f.rating ?? 5)),
            text: typeof f.comment === 'string' ? f.comment : '',
            submitted: Boolean(f.submitted),
          };
        });
        setPracticeFeedbacks(fb);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [course?.id, isDemoCourse, user?.id, isEnrolled]);

  useEffect(() => {
    const syncHeaderHeight = () => {
      const currentHeaderTop = readStickyHeaderHeight();
      setStickyHeaderTop((prev) => (prev === currentHeaderTop ? prev : currentHeaderTop));
    };
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);
    return () => window.removeEventListener('resize', syncHeaderHeight);
  }, []);

  useEffect(() => {
    const handleTabsDocking = () => {
      if (window.innerWidth < 1024) {
        return;
      }
      const currentHeaderTop = readStickyHeaderHeight();
      setStickyHeaderTop((prev) => (prev === currentHeaderTop ? prev : currentHeaderTop));
      const tabsTop = tabsBarRef.current?.getBoundingClientRect().top ?? 0;
      const gridBottom = tabsGridContainerRef.current?.getBoundingClientRect().bottom ?? Number.NEGATIVE_INFINITY;
      const passedTopTrigger = tabsTop <= currentHeaderTop + 4;
      const fadeStart = 340;
      const fadeEnd = 220;
      const opacityProgress = Math.max(0, Math.min(1, (gridBottom - fadeEnd) / (fadeStart - fadeEnd)));
      // Keep sidebar tabs visible only within the two-column tabs container bounds.
      setIsTabsDockedToSidebar(passedTopTrigger && gridBottom > fadeEnd);
      setTabsDockOpacity(passedTopTrigger ? opacityProgress : 0);
    };

    handleTabsDocking();
    window.addEventListener('scroll', handleTabsDocking, { passive: true });
    window.addEventListener('resize', handleTabsDocking);
    return () => {
      window.removeEventListener('scroll', handleTabsDocking);
      window.removeEventListener('resize', handleTabsDocking);
    };
  }, []);

  useEffect(() => {
    if (!isTabsDockedToSidebar || hasPlayedTabsDockIntro) return;
    setHasPlayedTabsDockIntro(true);
    setIsTabsDockIntroAnimating(true);
    const timer = window.setTimeout(() => setIsTabsDockIntroAnimating(false), 900);
    return () => window.clearTimeout(timer);
  }, [isTabsDockedToSidebar, hasPlayedTabsDockIntro]);

  const scrollToSectionWithOffset = useCallback(
    (el: HTMLElement | null, offset?: number) => {
      if (!el || isMobileCourseLayout()) return;
      const safeOffset = offset ?? stickyHeaderTop + 28;
      const y = window.scrollY + el.getBoundingClientRect().top - safeOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    },
    [stickyHeaderTop],
  );

  const handleTabSelect = useCallback((tab: typeof activeTab) => {
    setActiveTab(tab);
  }, []);

  /** ورود مستقیم به بخش پخش دوره (سرفصل‌ها) برای کاربران ثبت‌نام‌کرده؛ روی موبایل هم اسکرول می‌کند */
  const handleGoToPlayback = useCallback(() => {
    setActiveTab('curriculum');
    window.requestAnimationFrame(() => {
      const el = tabContentStartRef.current;
      if (!el) return;
      if (isMobileCourseLayout()) {
        const y = window.scrollY + el.getBoundingClientRect().top - 80;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      } else {
        scrollToSectionWithOffset(el);
      }
    });
  }, [scrollToSectionWithOffset]);

  useEffect(() => {
    if (!hasMountedTabChangeRef.current) {
      hasMountedTabChangeRef.current = true;
      return;
    }
    scrollToSectionWithOffset(tabContentStartRef.current);
  }, [activeTab, scrollToSectionWithOffset]);

  useEffect(() => {
    if (activeTab !== 'comments' || !isDemoCourse) return;
    if (!hasMountedCommentFilterRef.current) {
      hasMountedCommentFilterRef.current = true;
      return;
    }
    scrollToSectionWithOffset(commentsListRef.current, stickyHeaderTop + 40);
  }, [
    activeTab,
    isDemoCourse,
    commentQuery,
    commentRateFilter,
    commentRoleFilter,
    commentWithReplyOnly,
    commentHelpfulOnly,
    commentEngagementFilter,
    stickyHeaderTop,
    scrollToSectionWithOffset,
  ]);

  const handleEnroll = async () => {
    if (isDemoCourse) {
      setIsEnrolled(true);
      const params = new URLSearchParams(location.search);
      params.set('demo', 'buyer');
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    setEnrolling(true);
    try {
      // Check if course is free or paid
      if (Number(course?.price) === 0) {
        await coursesService.enroll(course!.id);
        setIsEnrolled(true);
        // Refresh course data to show videos
        await fetchCourse();
      } else {
        // Paid course - initiate payment
        const couponPaymentOpts =
          checkoutCouponFromQuery != null && course!.id
            ? {
                couponCode: checkoutCouponFromQuery,
                couponCartCourseIds: [course!.id],
              }
            : undefined;
        const paymentData = await paymentsService.initiateCoursePayment(course!.id, couponPaymentOpts);
        
        if (paymentData.enrolled) {
          // Already purchased or wallet balance was enough
          setIsEnrolled(true);
          await fetchCourse();
        } else if (paymentData.paymentUrl) {
          // PayPing's payment URL already carries the payment code, so a
          // plain redirect is enough.
          window.location.href = paymentData.paymentUrl;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در فرآیند ثبت‌نام');
    } finally {
      setEnrolling(false);
    }
  };

  const isCourseInCart = course ? hasItem(course.id) : false;

  const handleAddToCart = () => {
    if (!course || isDemoCourse || isCourseInCart) return;

    addItem({
      id: course.id,
      title: course.title,
      subtitle: 'دسترسی ۱۲ ماهه · ویدیو + فایل',
      price: Math.max(0, Math.round(payablePrice)),
      listPrice: Math.max(0, Math.round(displayListPrice)),
      thumbnail: getImageUrlWithFallback(course.thumbnail, '/assets/homeV2/01.jpg'),
    });

    setCartToastThumb(getImageUrlWithFallback(course.thumbnail, '/assets/homeV2/01.jpg'));
    setShowCartToast(true);
    if (cartToastTimerRef.current != null) {
      window.clearTimeout(cartToastTimerRef.current);
    }
    cartToastTimerRef.current = window.setTimeout(() => {
      setShowCartToast(false);
      cartToastTimerRef.current = null;
    }, 3200);
  };

  useEffect(() => {
    return () => {
      if (cartToastTimerRef.current != null) {
        window.clearTimeout(cartToastTimerRef.current);
      }
    };
  }, []);

  const handleCopyCourseLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch {
      // Clipboard may be unavailable in some browsers/contexts.
      setShareCopied(false);
    }
  };

  const handleShareCourse = async () => {
    setIsShareModalOpen(true);
  };

  const handleOpenShareTarget = (url: string) => {
    if (url.startsWith('http') || url.startsWith('mailto:')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.href = url;
  };

  const handleVideoClick = (videoId: string) => {
    if (isDemoCourse || !course) return;
    if (!user || !isEnrolled) {
      if (!user) {
        navigate('/login', { state: { from: `${location.pathname}${location.search}` } });
      }
      return;
    }
    navigate(`/courses/${getCoursePathSegment(course)}/videos/${videoId}`);
  };

  const handleAudioClick = (audioId: string) => {
    if (isDemoCourse || !course) return;
    if (!user || !isEnrolled) {
      if (!user) {
        navigate('/login', { state: { from: `${location.pathname}${location.search}` } });
      }
      return;
    }
    navigate(`/courses/${getCoursePathSegment(course)}/audios/${audioId}`);
  };

  const averageDemoRating = useMemo(() => {
    if (!demoComments.length) return 0;
    return demoComments.reduce((sum, item) => sum + item.rating, 0) / demoComments.length;
  }, [demoComments]);

  const filteredDemoComments = useMemo(() => {
    let items = [...demoComments];

    if (commentRateFilter !== 'all') {
      items = items.filter((item) => item.rating === commentRateFilter);
    }

    if (commentQuery.trim()) {
      const q = commentQuery.trim().toLowerCase();
      items = items.filter(
        (item) => item.author.toLowerCase().includes(q) || item.content.toLowerCase().includes(q)
      );
    }

    if (commentRoleFilter !== 'all') {
      items = items.filter((item) => {
        if (commentRoleFilter === 'participant') return item.role.includes('شرکت');
        if (commentRoleFilter === 'manager') return item.role.includes('مدیر');
        if (commentRoleFilter === 'entrepreneur') return item.role.includes('کارآفرین');
        return true;
      });
    }

    if (commentWithReplyOnly) {
      items = items.filter((item) => item.hasInstructorReply);
    }

    if (commentHelpfulOnly) {
      items = items.filter((item) => (item.likes || 0) >= 10);
    }

    if (commentEngagementFilter === 'liked') {
      items = items.filter((item) => (item.likes || 0) > (item.dislikes || 0));
    }

    if (commentEngagementFilter === 'disliked') {
      items = items.filter((item) => (item.dislikes || 0) >= 3);
    }

    if (commentSort === 'topRated') {
      items.sort((a, b) => b.rating - a.rating);
    } else if (commentSort === 'mostHelpful') {
      items.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    } else {
      items.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
    }

    return items;
  }, [demoComments, commentQuery, commentRateFilter, commentSort, commentRoleFilter, commentWithReplyOnly, commentHelpfulOnly, commentEngagementFilter]);
  const activeCommentFiltersCount =
    (commentRateFilter !== 'all' ? 1 : 0) +
    (commentRoleFilter !== 'all' ? 1 : 0) +
    (commentWithReplyOnly ? 1 : 0) +
    (commentHelpfulOnly ? 1 : 0) +
    (commentEngagementFilter !== 'all' ? 1 : 0);
  const faqItems = useMemo(() => {
    const list = course?.faqs;
    return Array.isArray(list) ? [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)) : [];
  }, [course?.faqs]);
  const filteredFaqs = faqItems.filter((item) => {
    const cat = item.category === 'payment' || item.category === 'access' || item.category === 'learning' ? item.category : 'learning';
    const passCategory = faqCategory === 'all' || cat === faqCategory;
    const q = faqQuery.trim().toLowerCase();
    const passQuery =
      !q ||
      item.question.toLowerCase().includes(q) ||
      item.answer.toLowerCase().includes(q);
    return passCategory && passQuery;
  });

  useEffect(() => {
    const list = course?.faqs;
    if (!Array.isArray(list) || list.length === 0) {
      setExpandedFaqId('');
      return;
    }
    const sorted = [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    setExpandedFaqId((prev) => (prev && sorted.some((f) => f.id === prev) ? prev : sorted[0].id));
  }, [course?.faqs]);

  type CurriculumLessonRow = {
    lessonRowId: string;
    title: string;
    caption: string;
    duration?: number;
    orderLabel: string;
    videoId?: string;
    audioId?: string;
    kinds: string[];
  };

  const curriculumTracks = useMemo((): Array<{
    id: string | number;
    title: string;
    subtitle: string;
    lessons: CurriculumLessonRow[];
  }> => {
    if (!course) return [];
    const apiChapters = course.chapters;
    if (!isDemoCourse && Array.isArray(apiChapters) && apiChapters.length > 0) {
      return apiChapters.map((chapter) => ({
        id: chapter.id,
        title: chapter.title,
        subtitle: (chapter.description || '').trim() || ' ',
        lessons: chapter.lessons.map((lesson, lessonIdx): CurriculumLessonRow => {
          const v = lesson.video;
          const a = lesson.audio;
          const durationSec =
            v?.duration != null && a?.duration != null
              ? Math.max(v.duration ?? 0, a.duration ?? 0)
              : v?.duration || a?.duration || 0;
          const kinds: string[] = [];
          if (v) kinds.push('ویدیو');
          if (a) kinds.push('صوت');
          if (!kinds.length) kinds.push('متن');
          return {
            lessonRowId: lesson.id,
            title: lesson.title,
            caption: (lesson.caption || '').trim(),
            duration: durationSec,
            orderLabel: `جلسه ${lessonIdx + 1}`,
            videoId: v?.id,
            audioId: a?.id,
            kinds,
          };
        }),
      }));
    }

    const fallback = [
      {
        id: 0,
        title: 'فصل اول: ذهن و شفافیت مسیر',
        subtitle: 'زیرساخت ذهنی برای شروع قدرتمند',
        lessons: [
          ...(course.videos || []).slice(0, 2).map((item, idx): CurriculumLessonRow => ({
            lessonRowId: item.id,
            title: item.title,
            caption: item.description || '',
            duration: item.duration,
            orderLabel: `جلسه ${idx + 1}`,
            videoId: item.id,
            audioId: undefined,
            kinds: ['ویدیو'],
          })),
        ],
      },
      {
        id: 1,
        title: 'فصل دوم: بازطراحی الگوها',
        subtitle: 'تغییر رفتارهای تکرارشونده و فرساینده',
        lessons: [
          ...(course.videos || []).slice(2).map((item, idx): CurriculumLessonRow => ({
            lessonRowId: item.id,
            title: item.title,
            caption: item.description || '',
            duration: item.duration,
            orderLabel: `جلسه ${idx + 3}`,
            videoId: item.id,
            audioId: undefined,
            kinds: ['ویدیو'],
          })),
          ...(course.audios || []).map((item, idx): CurriculumLessonRow => ({
            lessonRowId: item.id,
            title: item.title,
            caption: item.description || '',
            duration: item.duration,
            orderLabel: `فایل صوتی ${idx + 1}`,
            videoId: undefined,
            audioId: item.id,
            kinds: ['صوت'],
          })),
        ],
      },
      {
        id: 2,
        title: 'فصل سوم: تصمیم‌گیری و اجرای پایدار',
        subtitle: 'تبدیل آگاهی به اقدام مستمر',
        lessons: [
          {
            lessonRowId: 'demo-extra-v-1',
            title: 'جلسه: مدل تصمیم‌گیری ۳ مرحله‌ای',
            caption: 'یاد می‌گیرید در شرایط پیچیده، سریع و دقیق تصمیم بگیرید.',
            duration: 1680,
            orderLabel: 'جلسه ۵',
            videoId: 'demo-extra-v-1',
            kinds: ['ویدیو'],
          },
          {
            lessonRowId: 'demo-extra-a-1',
            title: 'فایل صوتی: مرور هفتگی اجرای شخصی',
            caption: 'مرور فشرده برای حفظ استمرار و پیگیری برنامه.',
            duration: 720,
            orderLabel: 'فایل صوتی ۲',
            audioId: 'demo-extra-a-1',
            kinds: ['صوت'],
          },
        ],
      },
      {
        id: 3,
        title: 'فصل چهارم: تثبیت عادت‌ها و رشد بلندمدت',
        subtitle: 'چارچوب پایدار برای حفظ مسیر پیشرفت',
        lessons: [
          {
            lessonRowId: 'demo-extra-v-2',
            title: 'جلسه: طراحی سیستم شخصی رشد ۹۰ روزه',
            caption: 'یک نقشه عملی ۹۰ روزه برای استمرار در رشد فردی و حرفه‌ای.',
            duration: 2040,
            orderLabel: 'جلسه ۶',
            videoId: 'demo-extra-v-2',
            kinds: ['ویدیو'],
          },
          {
            lessonRowId: 'demo-extra-v-3',
            title: 'جلسه: مدیریت افت انگیزه و بازگشت سریع',
            caption: 'تکنیک‌های بازیابی تمرکز بعد از وقفه و افت انرژی.',
            duration: 1560,
            orderLabel: 'جلسه ۷',
            videoId: 'demo-extra-v-3',
            kinds: ['ویدیو'],
          },
        ],
      },
    ];

    return fallback.filter((track) => track.lessons.length > 0);
  }, [course, isDemoCourse]);

  const curriculumTotals = useMemo(() => {
    let lessonCount = 0;
    let minutes = 0;
    curriculumTracks.forEach((t) =>
      t.lessons.forEach((l) => {
        lessonCount += 1;
        minutes += Math.floor((l.duration || 0) / 60);
      }),
    );
    return { lessonCount, minutes };
  }, [curriculumTracks]);

  /** اولین جلسه‌ای که ویدیو یا صوت دارد؛ برای دکمهٔ «تماشای دوره» که مستقیم وارد پلیر می‌شود */
  const firstPlayableLesson = useMemo(() => {
    for (const track of curriculumTracks) {
      for (const lesson of track.lessons) {
        if (lesson.videoId || lesson.audioId) return lesson;
      }
    }
    return null;
  }, [curriculumTracks]);

  /**
   * ورود مستقیم به پلیر پخش اولین جلسه (ویدیو در اولویت، سپس صوت)؛ اگر جلسه‌ای نبود به تب سرفصل‌ها می‌رود.
   * عمداً useCallback ندارد تا هر رندر مقادیر تازهٔ isEnrolled/user/course را ببندد (وگرنه با کلوژر کهنه کلیک بی‌اثر می‌شود).
   */
  const handleWatchCourse = () => {
    if (firstPlayableLesson?.videoId) {
      handleVideoClick(firstPlayableLesson.videoId);
    } else if (firstPlayableLesson?.audioId) {
      handleAudioClick(firstPlayableLesson.audioId);
    } else {
      handleGoToPlayback();
    }
  };

  const displayLessonUpdates = useMemo((): LessonUpdateDisplayRow[] => {
    if (!course) return [];
    const faDurLocal = (s: string) => String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
    const DEMO_AUDIO_SAMPLE =
      'https://interactive-examples.mdn.mozilla.net/media/cc0-audio/t-rex-roar.mp3';
    if (isDemoCourse) {
      const emb = FALLBACK_APARAT_DEMO_EMBED;
      return [
        {
          id: 'upd-1',
          title: 'افزودن درس جدید: تکنیک تصمیم‌گیری تحت فشار',
          notes: 'یک ویدیوی ۲۸ دقیقه‌ای به فصل دوم اضافه شد همراه با تمرین عملی و مثال واقعی.',
          status: 'جدید',
          kind: 'ویدیو',
          duration: '۲۸:۰۰',
          date: '۱۴۰۵/۰۲/۰۲',
          version: 'v1.4',
          apiKind: 'VIDEO',
          videoIframeSrc: emb,
          videoFileSrc: null,
          audioSrc: null,
        },
        {
          id: 'upd-2',
          title: 'بازنگری کامل جزوه فصل اول',
          notes: 'جزوه فصل اول با ساختار واضح‌تر، چک‌لیست اجرایی و نمودار مسیر مطالعه آپدیت شد.',
          status: 'بهبود',
          kind: 'ویدیو',
          duration: '۱۸:۳۰',
          date: '۱۴۰۵/۰۱/۲۳',
          version: 'v1.3',
          apiKind: 'VIDEO',
          videoIframeSrc: emb,
          videoFileSrc: null,
          audioSrc: null,
        },
        {
          id: 'upd-3',
          title: 'افزودن فایل صوتی مرور هفتگی',
          notes: 'برای مرور سریع، فایل صوتی کوتاه ۱۲ دقیقه‌ای به منابع دوره اضافه شد.',
          status: 'جدید',
          kind: 'صوت',
          duration: '۱۲:۰۰',
          date: '۱۴۰۵/۰۱/۱۵',
          version: 'v1.2',
          apiKind: 'AUDIO',
          videoIframeSrc: null,
          videoFileSrc: null,
          audioSrc: DEMO_AUDIO_SAMPLE,
        },
      ];
    }
    const list = course.lessonUpdates || [];
    return list.map((u) => {
      const videoFileSrc = u.videoFile || null;
      const videoIframeSrc = videoFileSrc ? null : aparatShareToEmbedUrl(u.videoUrl);
      const audioSrc =
        typeof u.audioFile === 'string' && u.audioFile.trim().length > 0
          ? u.audioFile
          : typeof u.audioUrl === 'string' && u.audioUrl.trim().length > 0
            ? u.audioUrl.trim()
            : null;
      let dateStr = '—';
      try {
        const t = Date.parse(u.occurredAt);
        if (!Number.isNaN(t)) dateStr = new Date(t).toLocaleDateString('fa-IR');
      } catch {
        dateStr = '—';
      }
      return {
        id: u.id,
        title: u.title,
        notes: u.description || '',
        status: lessonUpdateBadgeLabel(u.badge),
        kind: lessonUpdateKindLabel(u.contentKind),
        duration: u.durationLabel ? faDurLocal(u.durationLabel) : '—',
        date: dateStr,
        version: u.versionLabel ? faDurLocal(u.versionLabel) : '—',
        apiKind: u.contentKind,
        videoIframeSrc,
        videoFileSrc,
        audioSrc,
      };
    });
  }, [course, isDemoCourse]);

  const displayPracticeSessions = useMemo((): CoursePracticeSessionPublic[] => {
    if (!course) return [];
    if (isDemoCourse) return DEMO_PRACTICE_SESSIONS;
    const raw = course.practiceSessions;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((s) => ({
      id: s.id,
      title: s.title,
      sortOrder: s.sortOrder ?? 0,
      pamphletUrl: s.pamphletUrl ?? null,
      tasks: [...(s.tasks || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    }));
  }, [course, isDemoCourse]);

  useEffect(() => {
    if (displayPracticeSessions.length === 0) {
      setActivePracticeSessionId('');
      return;
    }
    setActivePracticeSessionId((prev) =>
      prev && displayPracticeSessions.some((s) => s.id === prev)
        ? prev
        : displayPracticeSessions[0].id,
    );
  }, [displayPracticeSessions]);

  const activePracticePamphletUrl = useMemo(() => {
    const session =
      displayPracticeSessions.find((s) => s.id === activePracticeSessionId) ??
      displayPracticeSessions[0];
    return session?.pamphletUrl ?? '';
  }, [displayPracticeSessions, activePracticeSessionId]);

  /**
   * فایل جزوه را به‌صورت blob می‌گیریم و همان را در iframe نشان می‌دهیم؛
   * این‌طور نه به هدر X-Frame-Options دامنهٔ API وابسته‌ایم و نه به درست بودن Content-Type آن.
   * اگر fetch نشد (مثلاً CORS)، به لینک مستقیم fallback می‌کنیم.
   */
  useEffect(() => {
    if (!isPracticePdfModalOpen || !activePracticePamphletUrl) return;
    let cancelled = false;
    let objectUrl = '';
    setPracticePdfLoading(true);
    setPracticePdfFailed(false);
    setPracticePdfBlobUrl('');

    fetch(activePracticePamphletUrl, { credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdfBlob);
        setPracticePdfBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPracticePdfFailed(true);
      })
      .finally(() => {
        if (!cancelled) setPracticePdfLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isPracticePdfModalOpen, activePracticePamphletUrl]);

  const pricingPresentation = useMemo(() => {
    if (!course) {
      return {
        displayListPrice: 0,
        payablePrice: 0,
        showPriceDiscount: false,
        discountPercentBadge: 0,
        countdownDeadlineMs: NaN as number,
        showDiscountCountdown: false,
        showPayableAsFree: false,
      };
    }
    const displayListPrice = Math.round(
      typeof course.listPrice === 'number' ? course.listPrice : Number(course.price) || 0,
    );
    const payablePrice = Math.round(Number(course.price) || 0);
    const showPriceDiscount =
      Boolean(course.hasActiveDiscount) && displayListPrice > payablePrice && payablePrice >= 0;
    const discountPercentBadge =
      showPriceDiscount && displayListPrice > 0
        ? Math.max(0, Math.round(((displayListPrice - payablePrice) / displayListPrice) * 100))
        : 0;

    let countdownDeadlineMs = NaN as number;
    if (course.discountEndsAt) {
      const t = Date.parse(course.discountEndsAt);
      if (!Number.isNaN(t)) countdownDeadlineMs = t;
    } else if (isDemoCourse) {
      countdownDeadlineMs = demoDiscountEndsAtMs;
    }

    const showDiscountCountdown =
      showPriceDiscount &&
      !Number.isNaN(countdownDeadlineMs) &&
      countdownDeadlineMs > Date.now();

    const showPayableAsFree =
      Boolean(course.isFreeCourse) ||
      (!showPriceDiscount && payablePrice <= 0 && !Boolean(course.hasActiveDiscount));

    return {
      displayListPrice,
      payablePrice,
      showPriceDiscount,
      discountPercentBadge,
      countdownDeadlineMs,
      showDiscountCountdown,
      showPayableAsFree,
    };
  }, [course, isDemoCourse, demoDiscountEndsAtMs]);

  useEffect(() => {
    if (!pricingPresentation.showDiscountCountdown || Number.isNaN(pricingPresentation.countdownDeadlineMs)) {
      setTimeLeftMs(0);
      return;
    }
    const deadline = pricingPresentation.countdownDeadlineMs;
    const tick = () => setTimeLeftMs(Math.max(0, deadline - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [pricingPresentation.showDiscountCountdown, pricingPresentation.countdownDeadlineMs]);

  if (loading) {
    return (
      <SiteBrandedLoader variant="fullscreen" tone="dark" size="lg" message="در حال بارگذاری دوره..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="bg-red-900/30 border border-red-500/30 text-red-400 px-4 py-3 rounded max-w-md">
          {error}
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">دوره یافت نشد</h2>
          <button
            onClick={() => navigate('/courses')}
            className="bg-yellow-400 text-black px-6 py-2 rounded-md hover:bg-yellow-500 transition-colors"
          >
            بازگشت به دوره‌ها
          </button>
        </div>
      </div>
    );
  }

  const imageSrc = getImageUrlWithFallback(course.thumbnail, '/assets/homeV2/01.jpg');
  const totalLessons = (course.videos?.length || 0) + (course.audios?.length || 0);
  const totalMinutes =
    (course.videos?.reduce((sum, item) => sum + Math.floor((item.duration || 0) / 60), 0) || 0) +
    (course.audios?.reduce((sum, item) => sum + Math.floor((item.duration || 0) / 60), 0) || 0);

  const learningOutcomesFromApi = (course.learningOutcomes?.filter((s) => s?.trim()) ?? []) as string[];
  const learningPoints = isDemoCourse
    ? DEFAULT_LEARNING_POINTS
    : learningOutcomesFromApi.length > 0
      ? learningOutcomesFromApi
      : DEFAULT_LEARNING_POINTS;

  const summaryFromApi = (course.summaryBullets?.filter((s) => s?.trim()) ?? []) as string[];
  const courseSummaryPoints = isDemoCourse
    ? DEFAULT_SUMMARY_BULLETS
    : summaryFromApi.length > 0
      ? summaryFromApi
      : DEFAULT_SUMMARY_BULLETS;
  const courseAccessFeatures = [
    { label: 'دسترسی مادام‌العمر به محتوای دوره', icon: faInfinity },
    { label: 'دسترسی در موبایل و دسکتاپ', icon: faDisplay },
    { label: 'به‌روزرسانی‌های آتی دوره', icon: faArrowsRotate },
    { label: 'امکان ثبت پرسش در بخش نظرات', icon: faComments },
  ];
  const relatedArticles = [
    {
      id: 'art-1',
      title: 'چطور عادت‌های کوچک، تغییرهای بزرگ می‌سازند؟',
      excerpt: 'یک راهنمای عملی برای ساخت عادت‌های پایدار و جلوگیری از رها کردن مسیر بعد از چند روز.',
      cover: '/assets/homeV2/01.jpg',
      category: 'عادت‌سازی',
      readTime: '۷ دقیقه',
    },
    {
      id: 'art-2',
      title: 'مدیریت تمرکز در دنیای پر از نوتیفیکیشن',
      excerpt: 'تکنیک‌های کاربردی برای حذف حواس‌پرتی و رسیدن به تمرکز عمیق در کارهای روزانه.',
      cover: '/assets/homeV2/Drifter.jpg',
      category: 'تمرکز',
      readTime: '۹ دقیقه',
    },
    {
      id: 'art-3',
      title: 'الگوی تصمیم‌گیری سریع اما دقیق برای شرایط پیچیده',
      excerpt: 'یک مدل تصمیم‌گیری مرحله‌ای که کمک می‌کند در موقعیت‌های مبهم، انتخاب بهتر داشته باشید.',
      cover: '/assets/homeV2/Artboard 2 copy 4.jpg',
      category: 'تصمیم‌گیری',
      readTime: '۶ دقیقه',
    },
    {
      id: 'art-4',
      title: 'چرا استمرار از انگیزه مهم‌تر است؟',
      excerpt: 'بررسی علمی و تجربه‌محور برای ساخت سیستم شخصی که حتی در روزهای کم‌انرژی هم کار کند.',
      cover: '/assets/homeV2/Emotional Podcast2.jpg',
      category: 'رشد فردی',
      readTime: '۸ دقیقه',
    },
  ];
  const normalizeCourseCharacteristics = (
    raw: Course['courseCharacteristics'],
  ): { label: string; value: string }[] | null => {
    if (!raw || !Array.isArray(raw)) return null;
    const rows = (raw as unknown[])
      .map((x) => {
        if (!x || typeof x !== 'object') return null;
        const o = x as Record<string, unknown>;
        return {
          label: String(o.label ?? '').trim(),
          value: String(o.value ?? '').trim(),
        };
      })
      .filter((x): x is { label: string; value: string } => !!x && (!!x.label || !!x.value));
    return rows.length ? rows : null;
  };

  const charFromApi = !isDemoCourse ? normalizeCourseCharacteristics(course.courseCharacteristics) : null;
  const FEATURE_CARD_TONES = ['blue', 'fuchsia', 'amber', 'emerald', 'violet', 'slate'] as const;

  const courseFeatureItems: { label: string; value: string; tone: (typeof FEATURE_CARD_TONES)[number] }[] = charFromApi
    ? charFromApi.map((row, idx) => ({
        label: row.label || '—',
        value: row.value || '—',
        tone: FEATURE_CARD_TONES[Math.min(idx, FEATURE_CARD_TONES.length - 1)],
      }))
    : [
        { label: 'تعداد جلسات', value: `${totalLessons.toLocaleString('fa-IR')} جلسه`, tone: 'blue' },
        { label: 'حجم محتوای آموزشی', value: `${totalMinutes.toLocaleString('fa-IR')} دقیقه`, tone: 'fuchsia' },
        {
          label: 'سطح دوره',
          value: isDemoCourse ? 'متوسط تا پیشرفته' : (course.courseLevel?.trim() || 'متوسط تا پیشرفته'),
          tone: 'amber',
        },
        {
          label: 'مدت پیشنهادی روزانه',
          value: isDemoCourse ? '۳۰ تا ۴۵ دقیقه' : (course.suggestedDailyTime?.trim() || '۳۰ تا ۴۵ دقیقه'),
          tone: 'emerald',
        },
        {
          label: 'نوع آموزش',
          value: isDemoCourse ? 'عملی + پروژه‌محور' : (course.teachingFormat?.trim() || 'عملی + پروژه‌محور'),
          tone: 'violet',
        },
        {
          label: 'خروجی نهایی',
          value: isDemoCourse ? 'سیستم رشد شخصی' : (course.finalOutcomeLabel?.trim() || 'سیستم رشد شخصی'),
          tone: 'slate',
        },
      ];
  const longCourseAboutText = isDemoCourse
    ? DEFAULT_LONG_COURSE_ABOUT
    : (course.longDescription?.trim() || course.description?.trim() || '');
  const aboutContentIsHtml =
    !isDemoCourse && longCourseAboutText.trim().length > 0 && /<\/?[a-z][\s\S]*>/i.test(longCourseAboutText.trim());
  const toFaDigits = (value: string | number) =>
    String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

  const satisfactionStoriesFromApi = course.satisfactionStories ?? [];
  const satisfactionStoriesResolved: CourseSatisfactionStoryPublic[] =
    satisfactionStoriesFromApi.length > 0
      ? satisfactionStoriesFromApi
      : isDemoCourse
        ? DEMO_SATISFACTION_STORIES_FALLBACK
        : [];

  const satisfactionKpisFromApi = course.satisfactionKpis ?? [];
  const satisfactionKpisResolved: CourseSatisfactionKpiPublic[] =
    satisfactionKpisFromApi.length > 0
      ? satisfactionKpisFromApi
      : isDemoCourse
        ? DEMO_SATISFACTION_KPIS_FALLBACK
        : [];

  const satisfactionAvgResolved =
    (course.satisfactionAvgLabel ?? '').trim() ||
    (isDemoCourse ? DEMO_SATISFACTION_AVG_FALLBACK : '');

  const showSatisfactionSection =
    satisfactionStoriesResolved.length > 0 ||
    satisfactionKpisResolved.length > 0 ||
    Boolean((course.satisfactionAvgLabel ?? '').trim());

  const practiceSessionsTotalTasks = displayPracticeSessions.reduce((sum, s) => sum + s.tasks.length, 0);
  const practiceSessionsDoneCount = displayPracticeSessions.reduce(
    (sum, s) => sum + s.tasks.filter((t) => completedPracticeTasks[t.id]).length,
    0,
  );
  const practiceSessionsWithPamphletCount = displayPracticeSessions.filter((s) =>
    Boolean(s.pamphletUrl),
  ).length;
  const pamphletSessionsStatValue =
    displayPracticeSessions.length === 0
      ? 0
      : practiceSessionsWithPamphletCount >= displayPracticeSessions.length
        ? 1
        : practiceSessionsWithPamphletCount;

  const activePracticeSession =
    displayPracticeSessions.find((item) => item.id === activePracticeSessionId) ??
    displayPracticeSessions[0];
  const activePracticeCompletedCount = activePracticeSession
    ? activePracticeSession.tasks.filter((t) => completedPracticeTasks[t.id]).length
    : 0;
  const activePracticeFeedback = activePracticeSession
    ? practiceFeedbacks[activePracticeSession.id] ?? { text: '', rating: 5, submitted: false }
    : { text: '', rating: 5, submitted: false };

  const heroIntroIframeSrc =
    aparatShareToEmbedUrl(course.introVideoAparatUrl) ||
    (isDemoCourse ? FALLBACK_APARAT_DEMO_EMBED : null);
  const hasUploadedIntroVideo = Boolean(!isDemoCourse && course.videoFile);
  const showIntroPlayback = Boolean(heroIntroIframeSrc || hasUploadedIntroVideo);
  const introStreamSrc =
    !isDemoCourse && course?.id ? `${API_ORIGIN}/api/courses/${course.id}/intro-video/stream` : '';
  const introVideoPoster = course?.thumbnail ? getImageUrlWithFallback(course.thumbnail) : undefined;

  const formatPersianStudyDuration = (totalSeconds: number) => {
    const m = Math.max(0, Math.round(totalSeconds / 60));
    if (m <= 0) return '—';
    if (m < 60) return `${toFaDigits(m)} دقیقه`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (rem === 0) return `${toFaDigits(h)} ساعت`;
    return `${toFaDigits(h)} ساعت و ${toFaDigits(rem)} دقیقه`;
  };

  const prerequisiteCourses = isDemoCourse
    ? [
        { id: 'pre-1', title: 'مبانی خودشناسی کاربردی', level: 'مقدماتی', duration: '۱ ساعت و ۴۵ دقیقه', required: true, cover: '/assets/homeV2/01.jpg', href: null as string | null },
        { id: 'pre-2', title: 'مدیریت تمرکز در عصر حواس‌پرتی', level: 'مقدماتی', duration: '۲ ساعت و ۱۰ دقیقه', required: false, cover: '/assets/homeV2/Drifter.jpg', href: null as string | null },
        { id: 'pre-3', title: 'عادت‌سازی حرفه‌ای برای اجرا', level: 'متوسط', duration: '۱ ساعت و ۳۰ دقیقه', required: false, cover: '/assets/homeV2/Artboard 2 copy 4.jpg', href: null as string | null },
      ]
    : (course.prerequisites || []).map((p) => ({
        id: p.id,
        title: p.course.title,
        level: p.course.courseLevel?.trim() || '—',
        duration: formatPersianStudyDuration(p.course.totalDurationSeconds ?? 0),
        required: p.requirementType === 'MANDATORY',
        cover: getImageUrlWithFallback(p.course.thumbnail, '/assets/homeV2/01.jpg'),
        href: `/courses/${getCoursePathSegment(p.course)}`,
      }));

  const {
    displayListPrice,
    payablePrice,
    showPriceDiscount,
    discountPercentBadge,
    showDiscountCountdown,
    showPayableAsFree,
  } = pricingPresentation;

  const courseProgressPercent = Math.min(100, Math.max(12, Math.round((totalLessons / 24) * 100)));
  const courseProgressSteps = [
    { label: 'آماده‌سازی و هدف‌گذاری', value: 'تکمیل شده' },
    { label: 'آموزش مفهومی', value: `${toFaDigits(Math.min(100, Math.round(courseProgressPercent * 1.1)))}٪` },
    { label: 'تمرین‌های عملی', value: `${toFaDigits(Math.max(10, courseProgressPercent - 8))}٪` },
    { label: 'مطالعه موردی و اجرا', value: `${toFaDigits(Math.max(8, courseProgressPercent - 14))}٪` },
    { label: 'جلسات دوره', value: `${toFaDigits(totalLessons)} جلسه` },
    { label: 'محتوای آموزشی', value: `${toFaDigits(totalMinutes)} دقیقه` },
    { label: 'آخرین بروزرسانی', value: new Date(course.updatedAt || Date.now()).toLocaleDateString('fa-IR') },
    { label: 'وضعیت دسترسی', value: isEnrolled ? 'فعال برای شما' : 'پس از ثبت‌نام فعال می‌شود' },
  ];
  const encodedShareUrl = encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '');
  const shareText = `دوره ${course.title}`;
  const encodedShareText = encodeURIComponent(shareText);
  const shareTargets = [
    { id: 'telegram', label: 'تلگرام', icon: faTelegram, url: `https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareText}` },
    { id: 'whatsapp', label: 'واتساپ', icon: faWhatsapp, url: `https://wa.me/?text=${encodedShareText}%20${encodedShareUrl}` },
    { id: 'x', label: 'ایکس', icon: faXTwitter, url: `https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedShareUrl}` },
    { id: 'linkedin', label: 'لینکدین', icon: faLinkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}` },
    { id: 'messenger', label: 'مسنجر', icon: faFacebookMessenger, url: `fb-messenger://share/?link=${encodedShareUrl}` },
    { id: 'eitaa', label: 'ایتا', icon: faPaperPlane, url: `eitaa://share/url?url=${encodedShareUrl}&text=${encodedShareText}` },
    { id: 'bale', label: 'بله', icon: faPaperPlane, url: `bale://share?text=${encodedShareText}%20${encodedShareUrl}` },
    { id: 'rubika', label: 'روبیکا', icon: faComments, url: `rubika://share?text=${encodedShareText}%20${encodedShareUrl}` },
    { id: 'splus', label: 'سروش پلاس', icon: faComments, url: `splus://share?text=${encodedShareText}%20${encodedShareUrl}` },
    { id: 'email', label: 'ایمیل', icon: faEnvelope, url: `mailto:?subject=${encodedShareText}&body=${encodedShareText}%0A${encodedShareUrl}` },
  ];
  const heroDescriptionText = `${course.description || ''} این مسیر با تمرین‌های کوتاه روزانه، بازخورد مرحله‌ای و سناریوهای واقعی طراحی شده تا یادگیری فقط در حد تئوری باقی نماند. در هر بخش، یک اقدام اجرایی مشخص دارید که به شما کمک می‌کند نتیجه را در زندگی و کار خودتان ببینید.`;
  const totalSeconds = Math.floor(timeLeftMs / 1000);
  const timerDays = Math.floor(totalSeconds / (60 * 60 * 24));
  const timerHours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const timerMinutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const timerSeconds = totalSeconds % 60;
  const formatTimerPart = (num: number) => num.toString().padStart(2, '0');
  const timerDaySegment = timerDays < 100 ? String(timerDays).padStart(2, '0') : String(timerDays);
  const discountCountdownLabel =
    `${toFaDigits(timerDaySegment)}:${toFaDigits(formatTimerPart(timerHours))}:${toFaDigits(formatTimerPart(timerMinutes))}:${toFaDigits(formatTimerPart(timerSeconds))} تا پایان تخفیف`;
  const formatRelativeDate = (iso: string) => {
    const diff = Date.now() - +new Date(iso);
    const day = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (day <= 0) return 'امروز';
    if (day === 1) return 'دیروز';
    return `${day} روز پیش`;
  };
  const handleAddDemoComment = () => {
    if (!newCommentText.trim()) return;
    const next: DemoComment = {
      id: `demo-c-${Date.now()}`,
      author: user?.firstName || 'کاربر جدید',
      role: 'شرکت‌کننده دوره',
      content: newCommentText.trim(),
      rating: newCommentRating,
      createdAt: new Date().toISOString(),
      helpfulCount: 0,
      likes: 0,
      dislikes: 0,
    };
    setDemoComments((prev) => [next, ...prev]);
    setNewCommentText('');
    setNewCommentRating(5);
  };
  const voteComment = (commentId: string, voteType: 'like' | 'dislike') => {
    setDemoComments((prev) =>
      prev.map((item) => {
        if (item.id !== commentId) return item;
        if (voteType === 'like') {
          return { ...item, likes: (item.likes || 0) + 1 };
        }
        return { ...item, dislikes: (item.dislikes || 0) + 1 };
      })
    );
  };
  const togglePracticeTask = async (_sessionId: string, taskId: string) => {
    void _sessionId;
    const prevDone = !!completedPracticeTasks[taskId];
    const nextDone = !prevDone;
    setCompletedPracticeTasks((p) => ({ ...p, [taskId]: nextDone }));
    if (!isDemoCourse && user?.id && isEnrolled && course?.id) {
      try {
        await coursesService.setPracticeTaskDone(course.id, taskId, nextDone);
      } catch {
        setCompletedPracticeTasks((p) => ({ ...p, [taskId]: prevDone }));
      }
    }
  };

  const updatePracticeFeedback = (
    sessionId: string,
    patch: Partial<{ text: string; rating: number; submitted: boolean }>,
  ) => {
    setPracticeFeedbacks((prev) => ({
      ...prev,
      [sessionId]: {
        text: prev[sessionId]?.text ?? '',
        rating: prev[sessionId]?.rating ?? 5,
        submitted: prev[sessionId]?.submitted ?? false,
        ...patch,
      },
    }));
  };

  const persistPracticeFeedback = async (sessionId: string, submittedFlag: boolean) => {
    const fb = practiceFeedbacks[sessionId] ?? { text: '', rating: 5, submitted: false };
    const rating = Math.min(5, Math.max(1, fb.rating));
    const comment = fb.text.trim() || undefined;
    if (!isDemoCourse && user?.id && isEnrolled && course?.id) {
      try {
        await coursesService.putPracticeFeedback(course.id, sessionId, {
          rating,
          comment,
          submitted: submittedFlag,
        });
        setPracticeFeedbacks((prev) => ({
          ...prev,
          [sessionId]: {
            ...(prev[sessionId] ?? { text: '', rating: 5, submitted: false }),
            text: fb.text,
            rating,
            submitted: submittedFlag,
          },
        }));
      } catch {
        /* وضعیت محلی دست‌نخورده می‌ماند */
      }
    } else if (submittedFlag) {
      setPracticeFeedbacks((prev) => ({
        ...prev,
        [sessionId]: { ...(prev[sessionId] ?? fb), submitted: true },
      }));
    }
  };

  return (
    <div className="course-detail-page min-h-screen bg-gradient-to-b from-[#F8FAFC] via-[#F5F8FF] to-[#F8FAFC] pb-24 pt-0 text-[#0F172A] sm:py-8" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mx-4 mb-6 w-[calc(100%+2rem)] overflow-hidden rounded-none border border-[#DDE5FF] bg-white/95 shadow-[0_10px_30px_rgba(28,59,155,0.08)] backdrop-blur-sm sm:mx-0 sm:w-auto sm:rounded-3xl">
          <nav
            aria-label="مسیر صفحه"
            className="course-hscroll flex flex-nowrap items-center gap-1.5 overflow-x-auto overscroll-x-contain px-4 pb-2.5 pt-2.5 text-[11px] leading-snug text-[#64748B] [-webkit-overflow-scrolling:touch] max-sm:[-ms-overflow-style:none] max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden sm:gap-2 sm:overflow-visible sm:px-8 sm:pb-4 sm:pt-8 sm:text-sm sm:leading-normal sm:flex-wrap"
          >
            <Link to="/" className="shrink-0 whitespace-nowrap transition hover:text-[#1C3B9B]">
              خانه
            </Link>
            <span className="shrink-0" aria-hidden>
              /
            </span>
            <Link to="/courses" className="shrink-0 whitespace-nowrap transition hover:text-[#1C3B9B]">
              دوره‌ها
            </Link>
            {course.category?.slug ? (
              <>
                <span className="shrink-0" aria-hidden>
                  /
                </span>
                <Link
                  to={`/courses?cat=${encodeURIComponent(course.category.slug)}`}
                  className="shrink-0 whitespace-nowrap transition hover:text-[#1C3B9B] sm:max-w-[14rem] sm:truncate"
                  title={course.category.name}
                >
                  {course.category.name}
                </Link>
              </>
            ) : null}
            <span className="shrink-0" aria-hidden>
              /
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold text-[#0F172A]" title={course.title}>
              {course.title}
            </span>
          </nav>
          <div className="px-4 pb-4 pt-0 sm:px-8 sm:pb-8 sm:pt-4">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
              <div className="lg:w-80 shrink-0">
              {showDiscountCountdown && (
              <div className="mb-3 rounded-lg bg-[#FBE4EA] px-3 py-2 text-center text-[14px] font-extrabold text-[#B91C1C] leading-relaxed">
                {discountCountdownLabel}
              </div>
              )}
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-slate-300 shadow-sm">
                <img
                  src={imageSrc}
                  alt={course.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/assets/homeV2/01.jpg';
                  }}
                />
                {showIntroPlayback && (
                  <button
                    type="button"
                    onClick={() => setIsIntroVideoModalOpen(true)}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 transition hover:bg-black/30 lg:hidden"
                    aria-label="پخش ویدیو معرفی"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md">
                      <FontAwesomeIcon icon={faPlay} />
                    </span>
                  </button>
                )}
              </div>

              {isEnrolled && (
                <button
                  type="button"
                  onClick={handleWatchCourse}
                  className="group relative mt-2.5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-l from-[#0EA5A5] to-[#0F766E] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_8px_18px_-6px_rgba(13,148,136,0.5)] transition-all duration-300 hover:brightness-[1.06] active:scale-[0.98]"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  />
                  <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                    <FontAwesomeIcon icon={faPlay} className="text-[9px] translate-x-[1px]" />
                  </span>
                  <span className="relative">تماشای دوره</span>
                </button>
              )}
            </div>
            <div className="flex-1">
              <h1 className="mb-3 text-[1.5rem] font-black leading-tight text-[#0F172A] sm:text-[2rem] md:text-[2.375rem]">{course.title}</h1>
              <div className="max-w-4xl text-[#334155]">
                <p className={`text-[15px] leading-7 sm:text-base sm:leading-8 ${showFullHeroDescription ? '' : 'line-clamp-3'}`}>{heroDescriptionText}</p>
                {heroDescriptionText.length > 180 && (
                  <button
                    type="button"
                    onClick={() => setShowFullHeroDescription((prev) => !prev)}
                    className="mt-1 font-bold text-[#0EA5A5] hover:text-[#0F766E] transition"
                  >
                    {showFullHeroDescription ? 'نمایش کمتر' : 'بیشتر'}
                  </button>
                )}
              </div>

              <div className="mt-5 flex flex-col items-start gap-2 text-[13px] text-slate-700 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:text-[15px]">
                <span className="inline-flex items-center gap-1.5 font-bold text-[#111827]">
                  4.6
                  <span className="text-amber-500">★ ★ ★ ★ ☆</span>
                  <span className="text-sm text-slate-500">(۱,۲۵۸ رضایت)</span>
                </span>
                <span className="hidden text-slate-400 sm:inline">•</span>
                <span className="inline-flex items-center gap-1.5 font-bold">
                  <FontAwesomeIcon icon={faUsers} className="text-slate-400" />
                  {toFaDigits('247,927')} همراه مسیر رشد
                </span>
                <span className="hidden text-slate-400 sm:inline">•</span>
                <span className="inline-flex items-center gap-1.5 font-bold">
                  <FontAwesomeIcon icon={faRotate} className="text-slate-400" />
                  برنامه گام‌به‌گام تحول فردی
                </span>
              </div>

              <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                {!isEnrolled && (
                  <div className="order-2 hidden flex-wrap items-center gap-1.5 lg:order-1 lg:flex lg:gap-2">
                    <button
                      onClick={handleAddToCart}
                      disabled={isCourseInCart}
                      className="rounded-lg bg-[#3A68C2] px-3 py-2 text-[12px] font-extrabold text-white transition hover:bg-[#345DAF] disabled:opacity-50 sm:px-4 sm:py-2.5 sm:text-sm"
                    >
                      <span className="inline-flex items-center gap-2">
                        <FontAwesomeIcon icon={faCartShopping} />
                        {isCourseInCart ? 'در سبد خرید' : 'افزودن به سبد سفارش'}
                      </span>
                    </button>
                    {showPriceDiscount && (
                    <span className="rounded-md bg-[#DC2626] px-2 py-0.5 text-[11px] font-black text-white">
                      ٪{toFaDigits(discountPercentBadge)}
                    </span>
                    )}
                    <span className="text-[12px] font-bold text-slate-700 sm:text-[14px]">هزینه آموزش</span>
                    {showPriceDiscount && (
                    <p className="text-[14px] text-slate-400 line-through sm:text-[18px]">
                      {displayListPrice.toLocaleString('fa-IR')}
                    </p>
                    )}
                    {showPayableAsFree ? (
                      <FreeBadge size="lg" />
                    ) : (
                      <p className="text-[22px] leading-none font-black text-[#0F172A] sm:text-[28px]">
                        {payablePrice.toLocaleString('fa-IR')}
                        <span className="mr-1 text-[13px] font-bold text-slate-700">تومان</span>
                      </p>
                    )}
                  </div>
                )}

                {isEnrolled && (
                  <div className="order-2 hidden items-center gap-1.5 lg:order-1 lg:flex">
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-emerald-700">
                      <FontAwesomeIcon icon={faCircleCheck} />
                      دسترسی شما فعال است
                    </span>
                  </div>
                )}

                <div className="order-1 w-full text-[11px] text-slate-500 lg:order-2">
                  <div className={`flex gap-2 ${!isEnrolled ? 'flex-col items-end' : 'items-center justify-between'}`}>
                    <div className="flex items-center gap-2 text-[#0EA5A5]">
                      <p className="text-[19px] font-extrabold sm:text-[22px]">فراز قورچیان</p>
                      <img src="/assets/homeV2/01.jpg" alt="مدرس دوره" className="h-8 w-8 rounded-full object-cover" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={handleCopyCourseLink}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold text-slate-600 transition hover:bg-slate-100"
                      >
                        <FontAwesomeIcon icon={faLink} className="text-[11px] text-slate-900" />
                        {shareCopied ? 'کپی شد' : 'کپی لینک'}
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={handleShareCourse}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold text-slate-600 transition hover:bg-slate-100"
                      >
                        <FontAwesomeIcon icon={faShareNodes} className="text-[11px] text-slate-900" />
                        اشتراک
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden">
          {!isEnrolled ? (
            <div className="border border-[#D6D9E0] bg-white p-2 shadow-[0_16px_34px_rgba(15,23,42,0.16)]">
              {showDiscountCountdown && (
              <div className="mb-2 rounded-md bg-[#FBE4EA] px-2.5 py-1 text-center text-[12px] font-extrabold text-[#B91C1C] leading-relaxed">
                {discountCountdownLabel}
              </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={handleAddToCart}
                  disabled={isCourseInCart}
                  className="rounded-lg bg-[#3A68C2] px-4 py-2.5 text-[13px] font-extrabold text-white transition hover:bg-[#345DAF] disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-2">
                    <FontAwesomeIcon icon={faCartShopping} className="text-[12px]" />
                    {isCourseInCart ? 'در سبد خرید' : 'افزودن به سبد'}
                  </span>
                </button>
                <div className="flex flex-col items-start gap-0.5">
                  <div className="flex items-center gap-1.5">
                    {showPriceDiscount && (
                    <span className="rounded-md bg-[#DC2626] px-1.5 py-0.5 text-[10px] font-black text-white">
                      ٪{toFaDigits(discountPercentBadge)}
                    </span>
                    )}
                    {showPriceDiscount && (
                    <p className="text-[13px] text-slate-400 line-through">{displayListPrice.toLocaleString('fa-IR')}</p>
                    )}
                  </div>
                  {showPayableAsFree ? (
                    <FreeBadge size="md" className="shadow-none" />
                  ) : (
                    <p className="text-[24px] leading-none font-black text-[#0F172A]">
                      {payablePrice.toLocaleString('fa-IR')}
                      <span className="mr-1 text-[12px] font-bold text-slate-700">تومان</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-emerald-200 bg-white p-2 shadow-[0_16px_34px_rgba(15,23,42,0.16)]">
              <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2">
                <div className="flex items-center gap-2 text-emerald-700">
                  <FontAwesomeIcon icon={faCircleCheck} className="text-sm" />
                  <p className="text-[12px] font-extrabold">دسترسی دوره برای شما فعال است</p>
                </div>
                <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-black text-emerald-700">
                  {toFaDigits(courseProgressPercent)}٪
                </span>
              </div>
              <button
                type="button"
                onClick={handleWatchCourse}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-l from-[#0EA5A5] to-[#0F766E] px-3 py-2 text-[12px] font-bold text-white shadow-[0_8px_18px_-6px_rgba(13,148,136,0.5)] transition-all active:scale-[0.98]"
              >
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20">
                  <FontAwesomeIcon icon={faPlay} className="text-[8px] translate-x-[1px]" />
                </span>
                تماشای دوره
              </button>
              <button
                type="button"
                onClick={() => handleTabSelect('practice')}
                className="course-interactive-btn mt-2 w-full rounded-lg bg-[#3A68C2] px-3 py-2 text-[12px] font-extrabold text-white transition-colors hover:bg-[#345DAF]"
              >
                ادامه یادگیری و تمرین‌ها
              </button>
            </div>
          )}
        </div>

        <div ref={tabsGridContainerRef} className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div className="order-2 lg:order-1 space-y-6">
            <div
              ref={tabsBarRef}
              className={`course-tabs-bar course-tabs-sticky sticky z-[25] border border-[#DDE5FF] bg-white p-2 shadow-[0_10px_24px_rgba(14,165,165,0.16)] rounded-none sm:rounded-2xl lg:rounded-2xl max-lg:w-full max-lg:max-w-full max-lg:mx-0 max-lg:transition-none lg:-mx-4 lg:w-[calc(100%+2rem)] lg:bg-white/95 lg:backdrop-blur-sm lg:transition-[opacity,transform] lg:duration-300 ${
                isTabsDockedToSidebar ? 'lg:opacity-0 lg:-translate-y-2 lg:pointer-events-none' : 'opacity-100'
              }`}
              style={{ top: `${tabsStickyTop}px` }}
            >
              <div className="course-hscroll course-tabs-scroll no-scrollbar flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:overflow-visible lg:pb-0">
                <button
                  type="button"
                  onClick={() => handleTabSelect('overview')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'overview' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  درباره دوره
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('prerequisites')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'prerequisites' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  پیش‌نیازها
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('curriculum')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'curriculum' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  سرفصل‌ها
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('comments')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'comments' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  نظرات
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('updates')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'updates' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  آپدیت جلسات
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('practice')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'practice' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  تمرین‌ها و جزوات
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSelect('faq')}
                  className={`course-tab-btn course-interactive-btn shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === 'faq' ? 'bg-[#1C3B9B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  سوالات متداول
                </button>
              </div>
            </div>
            <div
              className={`pointer-events-none hidden lg:block sticky z-30 h-0 transition-all duration-300 ${
                isTabsDockedToSidebar ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                top: `${sidebarDockTop}px`,
                opacity: tabsDockOpacity,
                transform: `translateY(${(1 - tabsDockOpacity) * 10}px)`,
              }}
            >
              <div className="absolute right-0 translate-x-[calc(100%+6px)]">
                <div
                  className={`pointer-events-auto relative flex w-fit flex-col items-center gap-2 p-1 ${
                    isTabsDockIntroAnimating ? 'tabs-dock-pop' : ''
                  }`}
                >
                  {isTabsDockIntroAnimating && <span className="tabs-dock-orbit" aria-hidden="true" />}
                  {[
                    { key: 'overview' as const, label: 'درباره دوره' },
                    { key: 'prerequisites' as const, label: 'پیش‌نیازها' },
                    { key: 'curriculum' as const, label: 'سرفصل‌ها' },
                    { key: 'comments' as const, label: 'نظرات' },
                    { key: 'updates' as const, label: 'آپدیت‌ها' },
                    { key: 'practice' as const, label: 'تمرین‌ها' },
                    { key: 'faq' as const, label: 'سوالات' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleTabSelect(tab.key)}
                      title={tab.label}
                      aria-label={tab.label}
                      className={`course-tab-btn w-fit self-center rounded-lg px-2.5 py-1.5 text-center text-[11px] font-bold transition-colors ${
                        activeTab === tab.key
                          ? 'bg-[#1C3B9B] text-white shadow-[0_6px_14px_rgba(28,59,155,0.22)]'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div ref={tabContentStartRef} />

            {(heroIntroIframeSrc || hasUploadedIntroVideo) && (
              <div className="-mx-4 w-[calc(100%+2rem)] overflow-hidden border border-slate-200 bg-white shadow-sm sm:mx-0 sm:w-auto sm:rounded-2xl">
                {heroIntroIframeSrc ? (
                  <div className="aspect-video w-full bg-black">
                    <iframe
                      src={heroIntroIframeSrc}
                      title="ویدیو معرفی دوره"
                      className="h-full w-full"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-black">
                    <CourseVideoPlayer src={introStreamSrc} poster={introVideoPoster} />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-[#DDE5FF] bg-white p-3 shadow-sm sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-4 border border-[#DDE5FF] bg-gradient-to-b from-white to-[#F8FAFF] p-3 sm:mb-6 sm:rounded-2xl sm:p-5">
                  <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4 sm:gap-3">
                    <h2 className="text-lg font-black text-[#0F172A] sm:text-2xl">
                      خصوصیات <span className="text-[#7C3AED]">دوره</span>
                    </h2>
                    <span className="rounded-full border border-[#C8D6FF] bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-extrabold text-[#1C3B9B] sm:px-2.5 sm:py-1 sm:text-[11px]">
                      نمای سریع دوره
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
                    {courseFeatureItems.map((item) => (
                      <div
                        key={item.label}
                        className="group rounded-lg border border-slate-200 bg-white p-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#BFD2FF] hover:shadow-[0_10px_18px_rgba(28,59,155,0.08)] sm:rounded-xl sm:p-3"
                      >
                        <p className="mb-1 text-[10px] font-semibold text-[#64748B] sm:text-[11px]">{item.label}</p>
                        <p className="text-[13px] font-extrabold text-[#0F172A] sm:text-sm">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <h2 className="mb-4 text-lg font-bold text-[#0F172A] sm:mb-5 sm:text-2xl">
                  اینجا چه چیزهایی <span className="text-[#7C3AED]">یاد می‌گیرید</span>
                </h2>
                <div className="grid gap-2.5 sm:grid-cols-2 sm:gap-4">
                  {learningPoints.map((item) => (
                    <div key={item} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 transition-all duration-300 hover:border-[#BFD2FF] hover:bg-white hover:shadow-[0_8px_16px_rgba(28,59,155,0.07)] sm:gap-3 sm:rounded-xl sm:p-4">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EAF0FF] text-[10px] font-black text-[#1C3B9B] sm:h-6 sm:w-6 sm:text-xs">✓</span>
                      <p className="text-sm leading-6 text-[#334155] sm:leading-7">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 border border-[#DDE5FF] bg-gradient-to-b from-white to-[#F7FAFF] p-4 shadow-[0_10px_24px_rgba(28,59,155,0.08)] sm:mt-6 sm:rounded-2xl sm:p-7">
                  <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#1C3B9B]" />
                      <h3 className="text-base font-extrabold text-[#0F172A] sm:text-xl">درباره این دوره</h3>
                    </div>
                    <span className="rounded-full border border-[#C8D6FF] bg-gradient-to-r from-[#EAF0FF] to-[#F2F6FF] px-2 py-0.5 text-[10px] font-extrabold text-[#1C3B9B] shadow-sm sm:px-3 sm:py-1 sm:text-[11px]">
                      نسخه جامع معرفی
                    </span>
                  </div>
                  {!aboutContentIsHtml && (
                    <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3 sm:mb-4 sm:rounded-xl sm:p-4">
                      <p className="text-sm font-medium leading-7 text-[#334155] sm:text-[15px] sm:leading-8">
                        این دوره برای افرادی است که می‌خواهند از «پراکندگی ذهنی و تصمیم‌های واکنشی» به «تمرکز عمیق و اجرای پایدار» برسند.
                      </p>
                    </div>
                  )}
                  {aboutContentIsHtml ? (
                    <div
                      className="course-long-html prose prose-sm max-w-none text-[#334155] sm:prose-base prose-p:leading-7 prose-headings:text-[#0F172A] prose-li:my-1"
                      dir="rtl"
                      dangerouslySetInnerHTML={{ __html: longCourseAboutText }}
                    />
                  ) : (
                    <div className="space-y-3 text-sm leading-7 text-[#334155] sm:space-y-4 sm:text-[15px] sm:leading-8">
                      {longCourseAboutText
                        .trim()
                        .split('\n\n')
                        .map((p) => p.trim())
                        .filter(Boolean)
                        .map((paragraph, idx) => (
                          <p key={`about-${idx}`} className={idx === 0 ? 'text-[15px] text-[#1E293B] sm:text-[16px]' : ''}>
                            {paragraph}
                          </p>
                        ))}
                    </div>
                  )}
                  <div className="mt-4 rounded-lg border-r-4 border-[#1C3B9B] bg-[#EFF4FF] p-3 sm:mt-5 sm:rounded-xl sm:p-4">
                    <p className="text-[13px] leading-6 text-[#1E3A8A] sm:text-sm sm:leading-7">
                      نتیجه نهایی این مسیر: تبدیل شدن به فردی با تمرکز بالا، تصمیم‌گیری دقیق، و اجرای پایدار در زندگی شخصی و حرفه‌ای؛
                      بدون وابستگی به انگیزه‌های مقطعی.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'prerequisites' && (
              <div className="-mx-4 min-w-0 w-[calc(100%+2rem)] overflow-hidden border border-[#DDE5FF] bg-white p-4 shadow-sm sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                      دوره‌های <span className="text-[#7C3AED]">پیش‌نیاز</span> این مسیر
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B] sm:text-sm">قبل از شروع این دوره، مسیر پیشنهادی آمادگی را مشاهده کنید</p>
                  </div>
                  <span className="hidden min-w-[80px] flex-1 self-center border-t border-slate-200/70 lg:block" />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => prerequisitesCarouselRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      aria-label="اسلاید قبلی پیش‌نیازها"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    <button
                      onClick={() => prerequisitesCarouselRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      aria-label="اسلاید بعدی پیش‌نیازها"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="text-[11px] px-2.5 py-1 rounded-full border border-slate-300 bg-white text-slate-700 font-bold">
                      {toFaDigits(prerequisiteCourses.length)} دوره
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2 overflow-hidden">
                  {prerequisiteCourses.length === 0 ? (
                    <p className="py-10 text-center text-sm text-[#64748B]">
                      برای این دوره هنوز پیش‌نیازی از طرف تیم آموزشی ثبت نشده است.
                    </p>
                  ) : (
                  <div
                    ref={prerequisitesCarouselRef}
                    dir="ltr"
                    className="course-hscroll no-scrollbar flex w-full min-w-0 max-w-full gap-1.5 overflow-x-auto px-0.5 pb-1 snap-x snap-mandatory items-stretch lg:gap-3 lg:px-1"
                  >
                  {prerequisiteCourses.map((item) => {
                    const cardClass =
                      'group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200/90 bg-white text-right shadow-[0_12px_20px_-18px_rgba(15,23,42,0.26)] transition hover:border-[#1C3B9B]/25 sm:min-w-[240px] sm:max-w-[240px] sm:rounded-[20px] lg:min-w-[260px] lg:max-w-[260px]';
                    const inner = (
                      <>
                        <div className="relative aspect-[16/9] w-full overflow-hidden">
                          <img
                            src={item.cover}
                            alt={item.title}
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-2.5">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full font-extrabold border ${
                                item.required
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}
                            >
                              {item.required ? 'ضروری' : 'پیشنهادی'}
                            </span>
                            <span className="text-[10px] text-[#64748B] font-semibold">{item.duration}</span>
                          </div>
                          <p className="text-[13px] font-bold leading-5 text-[#0F172A]">{item.title}</p>
                          <p className="mt-1 text-[11px] text-[#64748B]">سطح {item.level}</p>
                        </div>
                      </>
                    );
                    if (item.href) {
                      return (
                        <Link key={item.id} to={item.href} dir="rtl" className={`${cardClass} block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1C3B9B]`}>
                          {inner}
                        </Link>
                      );
                    }
                    return (
                      <div key={item.id} dir="rtl" className={cardClass}>
                        {inner}
                      </div>
                    );
                  })}
                  </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'curriculum' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-slate-200 bg-[linear-gradient(145deg,#FFFFFF_0%,#F9FBFF_100%)] p-3 shadow-[0_12px_28px_rgba(15,23,42,0.06)] sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 sm:mb-6 sm:gap-3">
                  <div>
                    <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                      محتوای <span className="text-[#7C3AED]">دوره</span>
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B] sm:text-sm">ساختار دوره به صورت فصل‌بندی شده همراه با دسترسی هوشمند</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-extrabold text-slate-700 sm:px-3 sm:py-1 sm:text-xs">
                      {curriculumTracks.length} فصل
                    </span>
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-extrabold text-slate-700 sm:px-3 sm:py-1 sm:text-xs">
                      {curriculumTotals.lessonCount.toLocaleString('fa-IR')} جلسه
                    </span>
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-extrabold text-slate-700 sm:px-3 sm:py-1 sm:text-xs">
                      {curriculumTotals.minutes.toLocaleString('fa-IR')} دقیقه
                    </span>
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3">
                  {curriculumTracks.map((track) => {
                    const isExpanded = expandedTrack === track.id;
                    const trackDuration = track.lessons.reduce((sum, lesson) => sum + Math.floor((lesson.duration || 0) / 60), 0);
                    const chapterLocked = !canConsumeLessonMedia;
                    const chapterHeaderInner = (
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1 text-right">
                          <div className="flex items-center justify-end gap-2 sm:gap-2.5">
                            {chapterLocked && (
                              <FontAwesomeIcon icon={faLock} className="shrink-0 text-sm text-slate-500" title="قفل" />
                            )}
                            <p className="truncate text-[14px] font-bold text-[#0F172A] sm:text-base">{track.title}</p>
                          </div>
                          <p className="mt-0.5 text-[11px] text-[#64748B] sm:mt-1 sm:text-xs">{track.subtitle}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                          <span className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1 sm:text-xs">
                            {track.lessons.length} درس
                          </span>
                          <span className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1 sm:text-xs">
                            {trackDuration} دقیقه
                          </span>
                          <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] text-[#1C3B9B] transition-transform duration-300 ${
                              isExpanded ? 'rotate-180' : ''
                            } ${chapterLocked ? 'opacity-40' : ''}`}
                          >
                            <FontAwesomeIcon icon={faChevronDown} />
                          </span>
                        </div>
                      </div>
                    );
                    return (
                      <div key={track.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_18px_rgba(124,58,237,0.10)]">
                        {chapterLocked ? (
                          <div
                            className="w-full cursor-not-allowed bg-gradient-to-r from-slate-50 to-slate-100 px-3 py-3 text-right sm:px-5 sm:py-4"
                            aria-disabled
                            role="group"
                          >
                            {chapterHeaderInner}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setExpandedTrack(isExpanded ? -1 : track.id)}
                            className="course-interactive-btn w-full bg-gradient-to-r from-white to-slate-50 px-3 py-3 text-right transition-colors hover:from-slate-50 hover:to-slate-100 sm:px-5 sm:py-4"
                          >
                            {chapterHeaderInner}
                          </button>
                        )}

                        {isExpanded && !chapterLocked && (
                          <div className="space-y-2 bg-white p-2.5 sm:p-4">
                            {track.lessons.map((lesson) => {
                              const canAccess = canConsumeLessonMedia;
                              return (
                              <div
                                key={lesson.lessonRowId}
                                className={`rounded-lg border px-2.5 py-2.5 transition sm:rounded-xl sm:px-4 sm:py-3 ${
                                  canAccess
                                    ? 'border-slate-200 hover:border-[#1C3B9B] hover:bg-slate-50'
                                    : 'border-slate-200 bg-slate-50/60'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="mb-1 flex items-center gap-1.5 sm:gap-2">
                                      <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-600 sm:px-2 sm:text-[11px]">
                                        {lesson.orderLabel}
                                      </span>
                                      <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-700 sm:px-2 sm:text-[11px]">
                                        {lesson.kinds.join(' · ')}
                                      </span>
                                      {!canAccess && (
                                        <span className="inline-flex items-center gap-1 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-bold text-slate-700 sm:px-2 sm:text-[11px]">
                                          <FontAwesomeIcon icon={faLock} className="text-[10px]" />
                                          قفل
                                        </span>
                                      )}
                                    </div>
                                    <p className="truncate text-[13px] font-semibold text-[#0F172A] sm:text-[15px]">{lesson.title}</p>
                                    {lesson.caption && <p className="mt-0.5 line-clamp-2 text-xs text-[#64748B] sm:mt-1 sm:text-sm">{lesson.caption}</p>}
                                  </div>
                                  <div className="text-left shrink-0">
                                    <p className="mb-1 text-[10px] text-[#64748B] sm:text-xs">
                                      {Math.floor((lesson.duration || 0) / 60)}:{Math.floor((lesson.duration || 0) % 60)
                                        .toString()
                                        .padStart(2, '0')}
                                    </p>
                                    <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-700 sm:px-2 sm:py-1 sm:text-[11px]">
                                      {canAccess ? 'قابل مشاهده' : (
                                        <span className="inline-flex items-center gap-1">
                                          <FontAwesomeIcon icon={faLock} className="text-[10px]" />
                                          قفل
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:mt-3 sm:gap-2">
                                  <button
                                    type="button"
                                    onClick={() => lesson.videoId && handleVideoClick(lesson.videoId)}
                                    disabled={!canAccess || !lesson.videoId}
                                    className={`course-interactive-btn inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold transition-colors sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-[11px] ${
                                      canAccess && lesson.videoId
                                        ? 'border-blue-200 bg-blue-50 text-[#1C3B9B] hover:bg-blue-100'
                                        : 'border-slate-200 bg-white text-slate-400 cursor-not-allowed'
                                    }`}
                                  >
                                    <FontAwesomeIcon icon={faCirclePlay} className="text-[12px]" />
                                    پخش ویدیو
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => lesson.audioId && handleAudioClick(lesson.audioId)}
                                    disabled={!canAccess || !lesson.audioId}
                                    className={`course-interactive-btn inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold transition-colors sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-[11px] ${
                                      canAccess && lesson.audioId
                                        ? 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                                        : 'border-slate-200 bg-white text-slate-400 cursor-not-allowed'
                                    }`}
                                  >
                                    <FontAwesomeIcon icon={faHeadphonesSimple} className="text-[12px]" />
                                    پخش صوت
                                  </button>
                                </div>
                              </div>
                            )})}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!canConsumeLessonMedia && (
                  <div className="mt-5 rounded-xl border border-[#C11277]/20 bg-[#FDF0F7] p-4">
                    <p className="text-sm text-[#7A1C5A] leading-7">
                      {!user && !isDemoCourse ? (
                        <>
                          برای باز کردن فصل‌ها و تماشای ویدیوها باید{' '}
                          <button
                            type="button"
                            className="font-black text-[#1C3B9B] underline-offset-2 hover:underline"
                            onClick={() => navigate('/login', { state: { from: `${location.pathname}${location.search}` } })}
                          >
                            وارد حساب
                          </button>{' '}
                          شوید؛ سپس با ثبت‌نام در دوره به همهٔ جلسات دسترسی خواهید داشت.
                        </>
                      ) : isDemoGuest ? (
                        <>
                          در حالت مهمان دمو، سرفصل‌ها و ویدیوها قفل است. برای تجربهٔ کامل، از نوار بالای صفحهٔ دمو حالت خریدار را انتخاب کنید یا وارد شوید.
                        </>
                      ) : (
                        <>
                          برای دسترسی کامل به همهٔ فصل‌ها و پخش ویدیو، در این دوره ثبت‌نام کنید. پس از ثبت‌نام، فصل‌ها باز می‌شوند و تمام جلسات در دسترس است.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'updates' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.06)] sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 sm:mb-6 sm:gap-3">
                  <div>
                    <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                      آپدیت <span className="text-[#7C3AED]">جلسات</span> دوره
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B] sm:text-sm">تغییرات جدید دوره، درس‌های اضافه شده و نسخه‌های به‌روزرسانی</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-extrabold text-slate-700 sm:px-3 sm:text-xs">
                    {toFaDigits(displayLessonUpdates.length)} بروزرسانی ثبت شده
                  </span>
                </div>

                {displayLessonUpdates.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
                    هنوز بروزرسانی جلسه‌ای برای این دوره ثبت نشده است.
                  </div>
                ) : (
                  <div className="space-y-2.5 sm:space-y-3">
                    {displayLessonUpdates.map((item) => {
                      const canAccessUpdate = canConsumeLessonMedia;
                      const hasVideoMedia = Boolean(item.videoFileSrc || item.videoIframeSrc);
                      const hasAudioMedia = Boolean(item.audioSrc);
                      /**
                       * دکمه بر اساس رسانهٔ واقعاً موجود ساخته می‌شود، نه فقط «نوع محتوا»؛
                       * وگرنه اگر مدیر صوت آپلود کند ولی نوع را روی ویدیو بگذارد، صوت اصلاً دیده نمی‌شد.
                       */
                      const renderVideoBtn = hasVideoMedia || item.apiKind === 'VIDEO' || item.apiKind === 'BOTH';
                      const renderAudioBtn = hasAudioMedia || item.apiKind === 'AUDIO' || item.apiKind === 'BOTH';
                      const showVideoBtn = canAccessUpdate && hasVideoMedia;
                      const showAudioBtn = canAccessUpdate && hasAudioMedia;

                      const openLessonVideo = () => {
                        if (!canAccessUpdate) return;
                        if (item.videoFileSrc) {
                          setLessonMediaModal({
                            title: item.title,
                            variant: 'video',
                            src: item.videoFileSrc,
                          });
                        } else if (item.videoIframeSrc) {
                          setLessonMediaModal({
                            title: item.title,
                            variant: 'iframe',
                            src: item.videoIframeSrc,
                          });
                        }
                      };

                      const openLessonAudio = () => {
                        if (!canAccessUpdate || !item.audioSrc) return;
                        setLessonMediaModal({
                          title: item.title,
                          variant: 'audio',
                          src: item.audioSrc,
                        });
                      };

                      return (
                        <div
                          key={item.id}
                          className="group relative rounded-xl border border-slate-200 bg-white p-3 shadow-[0_8px_18px_rgba(124,58,237,0.10)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D8CCFF] hover:shadow-[0_14px_26px_rgba(124,58,237,0.18)] sm:rounded-2xl sm:p-4"
                        >
                          <span className="absolute bottom-3 top-3 right-0 w-1 rounded-r-full bg-slate-200 transition-colors duration-300 group-hover:bg-slate-300 sm:bottom-4 sm:top-4" />
                          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1.5 sm:mb-2 sm:gap-2">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <span className="inline-flex h-2 w-2 rounded-full bg-slate-500" />
                              <p className="text-sm font-extrabold text-[#0F172A] sm:text-base">{item.title}</p>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-extrabold text-slate-700 sm:px-2.5 sm:py-1 sm:text-[11px]">
                              {canAccessUpdate ? (
                                item.status
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <FontAwesomeIcon icon={faLock} className="text-[10px]" />
                                  قفل
                                </span>
                              )}
                            </span>
                          </div>
                          <p className="text-xs leading-6 text-[#475569] sm:text-sm sm:leading-7">{item.notes}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:mt-3 sm:gap-2">
                            {renderVideoBtn && (
                              <button
                                type="button"
                                onClick={openLessonVideo}
                                disabled={!showVideoBtn}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold transition sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-[11px] ${
                                  showVideoBtn
                                    ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                    : 'cursor-not-allowed border-slate-200 bg-white text-slate-400 opacity-70'
                                }`}
                              >
                                <FontAwesomeIcon icon={faCirclePlay} className="text-[11px] sm:text-[12px]" />
                                پخش ویدیو
                              </button>
                            )}
                            {renderAudioBtn && (
                              <button
                                type="button"
                                onClick={openLessonAudio}
                                disabled={!showAudioBtn}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold transition sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-[11px] ${
                                  showAudioBtn
                                    ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                    : 'cursor-not-allowed border-slate-200 bg-white text-slate-400 opacity-70'
                                }`}
                              >
                                <FontAwesomeIcon icon={faHeadphonesSimple} className="text-[11px] sm:text-[12px]" />
                                پخش صوت
                              </button>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px] sm:mt-2 sm:gap-2 sm:text-[11px]">
                            <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1">
                              نوع: {item.kind}
                            </span>
                            <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1">
                              مدت: {item.duration}
                            </span>
                            <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1">
                              تاریخ: {item.date}
                            </span>
                            <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-bold text-slate-600 sm:rounded-lg sm:px-2 sm:py-1">
                              نسخه: {item.version}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'practice' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-[#DDE5FF] bg-gradient-to-b from-white to-[#F9FBFF] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)] sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 sm:mb-5 sm:gap-3">
                  <div>
                    <h2 className="text-xl font-black text-[#0F172A] sm:text-2xl">
                      تمرین‌ها و <span className="text-[#7C3AED]">جزوات</span> جلسات
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B] sm:text-sm">برای هر جلسه، جزوه PDF و چک‌لیست تمرین عملی در دسترس است</p>
                  </div>
                  <span className="rounded-full border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-100 px-2.5 py-1 text-[11px] font-extrabold text-[#1C3B9B] shadow-[0_4px_10px_rgba(28,59,155,0.12)] sm:px-3 sm:text-xs">
                    {toFaDigits(displayPracticeSessions.length)} جلسه عملی
                  </span>
                </div>

                <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4 sm:mb-5 sm:gap-2.5">
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center shadow-[0_4px_10px_rgba(15,23,42,0.04)] sm:rounded-xl sm:px-3 sm:py-2">
                    <p className="text-base font-black text-[#0F172A] sm:text-lg">
                      {toFaDigits(displayPracticeSessions.length)}
                    </p>
                    <p className="text-[11px] text-[#64748B]">جلسات تمرینی</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center shadow-[0_4px_10px_rgba(15,23,42,0.04)] sm:rounded-xl sm:px-3 sm:py-2">
                    <p className="text-base font-black text-[#0F172A] sm:text-lg">
                      {toFaDigits(practiceSessionsTotalTasks)}
                    </p>
                    <p className="text-[11px] text-[#64748B]">آیتم چک‌لیست</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center shadow-[0_4px_10px_rgba(15,23,42,0.04)] sm:rounded-xl sm:px-3 sm:py-2">
                    <p className="text-base font-black text-[#0F172A] sm:text-lg">
                      {toFaDigits(practiceSessionsDoneCount)}
                    </p>
                    <p className="text-[11px] text-[#64748B]">انجام‌شده</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center shadow-[0_4px_10px_rgba(15,23,42,0.04)] sm:rounded-xl sm:px-3 sm:py-2">
                    <p className="text-base font-black text-[#0F172A] sm:text-lg">
                      {toFaDigits(pamphletSessionsStatValue)}
                    </p>
                    <p className="text-[11px] text-[#64748B]">
                      {practiceSessionsWithPamphletCount > 0 &&
                      practiceSessionsWithPamphletCount >= displayPracticeSessions.length
                        ? 'PDF برای هر جلسه'
                        : practiceSessionsWithPamphletCount > 0
                          ? `${toFaDigits(practiceSessionsWithPamphletCount)} جلسه با جزوه`
                          : displayPracticeSessions.length > 0
                            ? 'بدون جزوه PDF'
                            : 'جزوه PDF'}
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <div
                    className={`transition ${!isEnrolled && displayPracticeSessions.length > 0 ? 'blur-[3px] opacity-60 pointer-events-none select-none' : ''}`}
                  >
                    {displayPracticeSessions.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 px-4 py-14 text-center text-sm leading-7 text-[#64748B]">
                        برای این دوره هنوز تمرین و جزوه‌ای منتشر نشده است. بعداً دوباره سر بزنید.
                      </div>
                    ) : (
                      activePracticeSession && (
                        <div className="grid gap-3 lg:grid-cols-[280px_1fr] sm:gap-4">
                          <div className="space-y-1.5 rounded-xl border border-[#DDE5FF] bg-gradient-to-b from-[#F8FAFF] to-white p-2.5 shadow-[0_8px_18px_rgba(28,59,155,0.08)] sm:space-y-2 sm:rounded-2xl sm:p-3">
                            {displayPracticeSessions.map((session) => {
                              const completed = session.tasks.filter((t) => completedPracticeTasks[t.id]).length;
                              const n = Math.max(session.tasks.length, 1);
                              const progressPercent = Math.round((completed / n) * 100);
                              const isActive = activePracticeSession.id === session.id;
                              return (
                                <button
                                  key={session.id}
                                  type="button"
                                  onClick={() => setActivePracticeSessionId(session.id)}
                                  disabled={!isEnrolled}
                                  className={`w-full rounded-lg border px-2.5 py-2 text-right transition-all duration-300 sm:rounded-xl sm:px-3 sm:py-2.5 ${
                                    isActive
                                      ? 'border-[#1C3B9B] bg-[#EEF4FF] shadow-[0_10px_18px_rgba(28,59,155,0.12)]'
                                      : 'border-slate-200 bg-white hover:border-[#C8D6FF] hover:bg-slate-50 hover:shadow-[0_8px_16px_rgba(15,23,42,0.06)]'
                                  } ${!isEnrolled ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                  <p className="line-clamp-2 text-xs font-extrabold text-[#0F172A] sm:text-sm">
                                    {session.title}
                                  </p>
                                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200 sm:mt-2">
                                    <span
                                      className="block h-full rounded-full bg-gradient-to-r from-[#1C3B9B] to-[#60A5FA]"
                                      style={{ width: `${progressPercent}%` }}
                                    />
                                  </div>
                                  <p className="mt-1 text-[10px] text-[#64748B] sm:text-[11px]">
                                    {toFaDigits(completed)} از {toFaDigits(session.tasks.length)} تمرین
                                  </p>
                                </button>
                              );
                            })}
                          </div>

                          <div className="rounded-xl border border-[#DDE5FF] bg-white p-3 shadow-[0_8px_20px_rgba(15,23,42,0.05)] sm:rounded-2xl sm:p-4">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-1.5 sm:mb-3 sm:gap-2">
                              <h3 className="text-base font-extrabold text-[#0F172A] sm:text-lg">
                                {activePracticeSession.title}
                              </h3>
                              <span className="rounded-full border border-[#C8D6FF] bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-extrabold text-[#1C3B9B] sm:text-[11px]">
                                جلسه فعال
                              </span>
                            </div>

                            <div className="mb-2.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5 sm:mb-3 sm:rounded-xl sm:p-3">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-extrabold text-[#0F172A] sm:text-sm">
                                  جزوه <span className="text-[#7C3AED]">جلسه</span>
                                </p>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (activePracticeSession.pamphletUrl) setIsPracticePdfModalOpen(true);
                                    }}
                                    disabled={!isEnrolled || !activePracticeSession.pamphletUrl}
                                    title={activePracticeSession.pamphletUrl ? undefined : 'فایل جزوه برای این جلسه بارگذاری نشده'}
                                    className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-extrabold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-xs"
                                  >
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    مشاهده جزوه PDF
                                  </button>
                                  {isEnrolled && activePracticeSession.pamphletUrl && (
                                    <a
                                      href={activePracticeSession.pamphletUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download={`${activePracticeSession.title}.pdf`}
                                      className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-extrabold text-emerald-700 transition hover:bg-emerald-100 sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-xs"
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                      دانلود
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="rounded-lg border border-[#DDD6FE] bg-[linear-gradient(180deg,#FCFBFF_0%,#F7F8FF_100%)] p-2.5 shadow-[0_10px_20px_rgba(124,58,237,0.08)] sm:rounded-xl sm:p-3">
                              <div className="mb-2 flex items-center justify-between sm:mb-3">
                                <p className="text-xs font-extrabold text-[#0F172A] sm:text-sm">
                                  چک‌لیست <span className="text-[#7C3AED]">تمرین عملی</span>
                                </p>
                                <span className="rounded-full border border-[#DDD6FE] bg-white px-2 py-0.5 text-[10px] font-bold text-[#6D28D9] sm:text-xs">
                                  {toFaDigits(activePracticeCompletedCount)} /{' '}
                                  {toFaDigits(activePracticeSession.tasks.length)}
                                </span>
                              </div>
                              <div className="space-y-2 sm:space-y-2.5">
                                {activePracticeSession.tasks.map((task) => {
                                  const checked = !!completedPracticeTasks[task.id];
                                  return (
                                    <label
                                      key={task.id}
                                      className={`group flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-all duration-300 sm:gap-2.5 sm:rounded-xl sm:px-3 sm:py-2.5 ${
                                        checked
                                          ? 'border-[#C4B5FD] bg-[#F5F3FF] shadow-[0_8px_16px_rgba(124,58,237,0.12)]'
                                          : 'border-slate-200 bg-white hover:border-[#DDD6FE] hover:shadow-[0_6px_14px_rgba(124,58,237,0.08)]'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={!isEnrolled}
                                        onChange={() => void togglePracticeTask(activePracticeSession.id, task.id)}
                                        className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-[#7C3AED] focus:ring-[#7C3AED]/30 sm:mt-1 sm:h-4 sm:w-4"
                                      />
                                      <span
                                        className={`text-xs leading-6 transition-colors sm:text-sm sm:leading-7 ${checked ? 'font-semibold text-[#5B21B6]' : 'text-[#334155] group-hover:text-[#1F2937]'}`}
                                      >
                                        {task.text}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                              <button
                                type="button"
                                disabled={!isEnrolled}
                                onClick={() =>
                                  void persistPracticeFeedback(activePracticeSession.id, true)
                                }
                                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[#C4B5FD] bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-[#6D28D9] transition hover:bg-[#F5F3FF] hover:shadow-[0_8px_16px_rgba(124,58,237,0.14)] disabled:cursor-not-allowed disabled:opacity-60 sm:mt-3 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2 sm:text-xs"
                              >
                                <FontAwesomeIcon icon={faClipboardCheck} />
                                ثبت نهایی تمرین این جلسه
                              </button>
                            </div>

                            <div className="mt-2.5 rounded-lg border border-slate-200 bg-white p-2.5 shadow-[0_4px_10px_rgba(15,23,42,0.04)] sm:mt-3 sm:rounded-xl sm:p-3">
                              <div className="mb-2 flex flex-wrap items-center justify-between gap-1.5 sm:gap-2">
                                <p className="text-xs font-extrabold text-[#0F172A] sm:text-sm">
                                  فرم <span className="text-[#7C3AED]">بازخورد</span> جلسه
                                </p>
                                {activePracticeFeedback.submitted && (
                                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold text-emerald-700 sm:px-2 sm:py-1 sm:text-[11px]">
                                    بازخورد ثبت شد
                                  </span>
                                )}
                              </div>

                              <div className="mb-2 flex items-center gap-1.5">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                                    disabled={!isEnrolled}
                                    onClick={() =>
                                      updatePracticeFeedback(activePracticeSession.id, {
                                        rating: star,
                                        submitted: false,
                                      })
                                    }
                                    className={`text-base transition disabled:cursor-not-allowed sm:text-lg ${star <= activePracticeFeedback.rating ? 'text-amber-500' : 'text-slate-300 hover:text-amber-300'}`}
                                    aria-label={`امتیاز ${star}`}
                                  >
                                    ★
                                  </button>
                                ))}
                                <span className="mr-2 text-[11px] text-[#64748B] sm:text-xs">امتیاز شما</span>
                              </div>

                              <textarea
                                value={activePracticeFeedback.text}
                                disabled={!isEnrolled}
                                onChange={(e) =>
                                  updatePracticeFeedback(activePracticeSession.id, {
                                    text: e.target.value,
                                    submitted: false,
                                  })
                                }
                                rows={3}
                                placeholder="تجربه شما از این تمرین را بنویسید..."
                                className="w-full rounded-lg border border-slate-300 bg-slate-50 p-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/25 focus:border-[#1C3B9B] sm:rounded-xl sm:p-3 sm:text-sm"
                              />

                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  disabled={!isEnrolled}
                                  onClick={() => {
                                    if (!activePracticeFeedback.text.trim()) return;
                                    void persistPracticeFeedback(activePracticeSession.id, true);
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2 sm:text-xs"
                                >
                                  <FontAwesomeIcon icon={faClipboardCheck} />
                                  ثبت بازخورد
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  {!isEnrolled && displayPracticeSessions.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-start justify-center rounded-2xl bg-white/55 pt-10 backdrop-blur-[3px] sm:items-center sm:pt-0">
                      <div className="mx-4 max-w-md rounded-xl border border-slate-200 bg-white/95 px-4 py-3 text-center shadow-[0_16px_34px_rgba(15,23,42,0.12)] sm:rounded-2xl sm:px-5 sm:py-4">
                        <p className="text-base font-extrabold text-slate-900">جزوات و تمرین‌های این بخش قفل است</p>
                        <p className="mt-1 text-sm leading-7 text-slate-600">
                          برای دسترسی کامل به فایل‌های PDF و تمرین‌های عملی، لطفا دوره را خریداری کنید.
                        </p>
                        <button
                          type="button"
                          onClick={handleEnroll}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#1C3B9B]/20 bg-[#1C3B9B] px-4 py-2 text-xs font-extrabold text-white transition hover:bg-[#16307F]"
                        >
                          <FontAwesomeIcon icon={faCartShopping} />
                          خرید دوره و فعال‌سازی
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-slate-200 bg-white p-4 shadow-sm sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                {isDemoCourse ? (
                  <div>
                    <div className="grid gap-3 sm:gap-4 lg:grid-cols-[300px_1fr]">
                      <div className="lg:sticky lg:z-30 lg:self-start" style={{ top: `${sidebarDockTop}px` }}>
                        <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:rounded-2xl sm:p-4">
                          <h3 className="mb-2 text-sm font-bold text-slate-900 sm:mb-3 sm:text-base">خلاصه و فیلتر</h3>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center sm:rounded-xl sm:p-4">
                          <p className="text-2xl font-black text-[#1C3B9B] sm:text-3xl">{toFaDigits(averageDemoRating.toFixed(1))}</p>
                          <p className="text-xs text-[#64748B] mt-1">میانگین امتیاز از ۵</p>
                          <div className="mt-1.5 text-base text-amber-500 sm:mt-2 sm:text-lg">{'★'.repeat(Math.round(averageDemoRating))}</div>
                          <p className="text-xs text-[#64748B] mt-1">{demoComments.length} نظر ثبت شده</p>
                        </div>
                          <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 sm:mt-4 sm:space-y-4 sm:pt-4">
                            <div>
                              <p className="mb-2 text-[11px] font-bold text-slate-500">امتیاز</p>
                              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                <button
                                  onClick={() => setCommentRateFilter('all')}
                                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                                    commentRateFilter === 'all'
                                      ? 'bg-[#1C3B9B] text-white shadow-[0_6px_14px_rgba(28,59,155,0.24)]'
                                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                  }`}
                                >
                                  همه
                                </button>
                                {[5, 4, 3].map((score) => (
                                  <button
                                    key={score}
                                    onClick={() => setCommentRateFilter(commentRateFilter === score ? 'all' : score)}
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                                      commentRateFilter === score
                                        ? 'bg-[#1C3B9B] text-white shadow-[0_6px_14px_rgba(28,59,155,0.24)]'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {score}★
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <p className="mb-2 text-[11px] font-bold text-slate-500">نقش</p>
                              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {[
                                  { key: 'all', label: 'همه' },
                                  { key: 'participant', label: 'شرکت‌کننده' },
                                  { key: 'manager', label: 'مدیر' },
                                  { key: 'entrepreneur', label: 'کارآفرین' },
                                ].map((roleOption) => (
                                  <button
                                    key={roleOption.key}
                                    onClick={() =>
                                      setCommentRoleFilter(
                                        roleOption.key as 'all' | 'participant' | 'manager' | 'entrepreneur'
                                      )
                                    }
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                                      commentRoleFilter === roleOption.key
                                        ? 'bg-slate-800 text-white'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {roleOption.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <p className="mb-2 text-[11px] font-bold text-slate-500">تعامل</p>
                              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {[
                                  { key: 'all', label: 'همه' },
                                  { key: 'liked', label: 'محبوب' },
                                  { key: 'disliked', label: 'نیازمند بررسی' },
                                ].map((engagementOption) => (
                                  <button
                                    key={engagementOption.key}
                                    onClick={() =>
                                      setCommentEngagementFilter(
                                        engagementOption.key as 'all' | 'liked' | 'disliked'
                                      )
                                    }
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                                      commentEngagementFilter === engagementOption.key
                                        ? 'bg-slate-800 text-white'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {engagementOption.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] font-semibold text-slate-700 sm:rounded-xl sm:px-2.5 sm:py-2 sm:text-xs">
                                <input
                                  type="checkbox"
                                  checked={commentWithReplyOnly}
                                  onChange={(e) => setCommentWithReplyOnly(e.target.checked)}
                                  className="rounded border-slate-300"
                                />
                                فقط پاسخ مدرس
                              </label>
                              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] font-semibold text-slate-700 sm:rounded-xl sm:px-2.5 sm:py-2 sm:text-xs">
                                <input
                                  type="checkbox"
                                  checked={commentHelpfulOnly}
                                  onChange={(e) => setCommentHelpfulOnly(e.target.checked)}
                                  className="rounded border-slate-300"
                                />
                                فقط لایک بالا (۱۰+)
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 sm:space-y-4">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:rounded-2xl sm:p-4">
                          <h3 className="mb-2 text-sm font-bold text-[#0F172A] sm:mb-3">نظر خودت رو ثبت کن</h3>
                          <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-3 sm:gap-2">
                            <span className="text-xs text-[#475569] sm:text-sm">امتیاز شما:</span>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setNewCommentRating(star)}
                                className={`text-lg sm:text-xl ${star <= newCommentRating ? 'text-amber-500' : 'text-slate-300'}`}
                              >
                                ★
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={newCommentText}
                            onChange={(e) => setNewCommentText(e.target.value)}
                            rows={3}
                            className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:p-3 sm:text-sm"
                            placeholder="تجربه‌ات از این دوره رو بنویس..."
                          />
                          <div className="mt-2 flex justify-end sm:mt-3">
                            <button
                              onClick={handleAddDemoComment}
                              className="rounded-md bg-gradient-to-r from-[#1C3B9B] to-[#C11277] px-3 py-1.5 text-xs font-semibold text-white sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm"
                            >
                              ثبت نظر
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3">
                          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                            <div className="relative w-full sm:max-w-xs">
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-slate-400">⌕</span>
                              <input
                                value={commentQuery}
                                onChange={(e) => setCommentQuery(e.target.value)}
                                placeholder="جستجو در نظرات..."
                                className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pr-9 pl-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:py-2 sm:pr-10 sm:pl-3 sm:text-sm"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={commentSort}
                                onChange={(e) => setCommentSort(e.target.value as 'newest' | 'topRated' | 'mostHelpful')}
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
                              >
                                <option value="newest">جدیدترین</option>
                                <option value="topRated">بیشترین امتیاز</option>
                                <option value="mostHelpful">مفیدترین</option>
                              </select>
                              <button
                                onClick={() => {
                                  setCommentRateFilter('all');
                                  setCommentRoleFilter('all');
                                  setCommentWithReplyOnly(false);
                                  setCommentHelpfulOnly(false);
                                  setCommentEngagementFilter('all');
                                  setCommentQuery('');
                                  setCommentSort('newest');
                                }}
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold hover:border-[#1C3B9B] hover:bg-[#F8FAFF] sm:rounded-xl sm:px-3 sm:py-2 sm:text-xs"
                              >
                                پاکسازی فیلتر
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 text-[11px] text-[#64748B] sm:gap-2 sm:text-xs">
                            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-semibold sm:px-2.5 sm:py-1">
                              {filteredDemoComments.length} نتیجه
                            </span>
                            {activeCommentFiltersCount > 0 && (
                              <span className="rounded-md bg-[#EAF0FF] px-2 py-0.5 font-semibold text-[#1C3B9B] sm:px-2.5 sm:py-1">
                                {activeCommentFiltersCount} فیلتر فعال
                              </span>
                            )}
                          </div>
                        </div>

                        <div ref={commentsListRef} className="space-y-3">
                          {filteredDemoComments.map((comment) => (
                            <div key={comment.id} className="rounded-lg border border-[#DDE5FF] bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.04)] sm:rounded-xl sm:p-4">
                              <div className="mb-2 flex items-start justify-between gap-2 sm:gap-3">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EAF0FF] text-xs font-bold text-[#1C3B9B] sm:h-10 sm:w-10">
                                    {comment.author.slice(0, 1)}
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-[#0F172A] sm:text-base">{comment.author}</p>
                                    <p className="text-[10px] text-[#64748B] sm:text-xs">{comment.role} • {formatRelativeDate(comment.createdAt)}</p>
                                  </div>
                                </div>
                                <div className="text-xs text-amber-500 sm:text-sm">{'★'.repeat(comment.rating)}</div>
                              </div>
                              <p className="text-sm leading-6 text-[#475569] sm:leading-7">{comment.content}</p>
                              {comment.hasInstructorReply && (
                                <div className="mt-2 rounded-lg border border-[#CFE0FF] bg-[#F6F9FF] p-2.5 sm:mt-3 sm:rounded-xl sm:p-3">
                                  <div className="flex items-start gap-2 sm:gap-3">
                                    <img
                                      src={comment.instructorReplyAvatar || '/assets/homeV2/02.jpg'}
                                      alt="پاسخ مدرس"
                                      className="h-10 w-10 rounded-md border border-[#BFD4FF] object-cover sm:h-12 sm:w-12 sm:rounded-lg"
                                    />
                                    <div className="flex-1">
                                      <div className="mb-1 flex items-center gap-1.5 sm:gap-2">
                                        <span className="rounded-md bg-[#1C3B9B] px-1.5 py-0.5 text-[10px] font-bold text-white sm:text-xs">
                                          پاسخ مدرس
                                        </span>
                                        <p className="text-[10px] font-bold text-[#1C3B9B] sm:text-xs">
                                          {comment.instructorReplyAuthor || 'مدرس دوره'}
                                        </p>
                                      </div>
                                      <p className="text-xs leading-6 text-[#334155] sm:text-sm">
                                        {comment.instructorReplyText || 'ممنون از بازخورد ارزشمند شما.'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="mt-2 flex items-center justify-between sm:mt-3">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                  <button
                                    onClick={() => voteComment(comment.id, 'like')}
                                    className="inline-flex items-center gap-1 text-[10px] rounded-md border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 px-2 py-1 font-bold text-emerald-700 hover:from-emerald-100 hover:to-green-100 sm:gap-1.5 sm:rounded-lg sm:px-3 sm:text-xs"
                                  >
                                    <FontAwesomeIcon icon={faThumbsUp} />
                                    ({comment.likes || 0})
                                  </button>
                                  <button
                                    onClick={() => voteComment(comment.id, 'dislike')}
                                    className="inline-flex items-center gap-1 text-[10px] rounded-md border border-rose-200 bg-gradient-to-r from-rose-50 to-red-50 px-2 py-1 font-bold text-rose-700 hover:from-rose-100 hover:to-red-100 sm:gap-1.5 sm:rounded-lg sm:px-3 sm:text-xs"
                                  >
                                    <FontAwesomeIcon icon={faThumbsDown} />
                                    ({comment.dislikes || 0})
                                  </button>
                                </div>
                                <button className="text-[11px] text-[#1C3B9B] hover:underline sm:text-xs">گزارش</button>
                              </div>
                            </div>
                          ))}
                          {filteredDemoComments.length === 0 && (
                            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-[#64748B]">
                              نظری با این فیلتر پیدا نشد.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <CourseTemplateComments
                    courseId={course.id}
                    allowComments={course.allowComments !== false}
                    isEnrolled={isEnrolled}
                    user={user}
                    onEnroll={handleEnroll}
                  />
                )}
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="-mx-4 w-[calc(100%+2rem)] border border-[#DDE5FF] bg-white p-4 shadow-[0_10px_26px_rgba(15,23,42,0.06)] sm:mx-0 sm:w-auto sm:rounded-2xl sm:p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 border-b border-slate-100 pb-3 sm:mb-5 sm:gap-3 sm:pb-4">
                  <div>
                    <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                      سوالات <span className="text-[#7C3AED]">متداول</span>
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B] sm:text-sm">پاسخ سریع به سوالات پرتکرار قبل از ثبت‌نام</p>
                  </div>
                  <span className="rounded-full border border-[#C8D6FF] bg-[#EEF4FF] px-2.5 py-1 text-[11px] font-extrabold text-[#1C3B9B] shadow-[0_4px_10px_rgba(28,59,155,0.12)] sm:px-3 sm:text-xs">
                    {faqCategory === 'all' && !faqQuery.trim()
                      ? `${faqItems.length} سوال`
                      : `${filteredFaqs.length} از ${faqItems.length}`}
                  </span>
                </div>

                {!loading && faqItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-sm text-[#64748B]">
                    هنوز سوالی برای این دوره ثبت نشده است. مدیر می‌تواند از پنل ادمین، تب «سوالات متداول» همین دوره را پر کند.
                  </div>
                ) : (
                <div className="grid gap-3 sm:gap-4 lg:grid-cols-[260px_1fr]">
                  <div className="space-y-2.5 rounded-xl border border-slate-200 bg-[#FCFDFF] p-3 shadow-[0_6px_18px_rgba(15,23,42,0.04)] sm:space-y-3 sm:rounded-2xl sm:p-4">
                    <div className="relative">
                      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 sm:right-3">⌕</span>
                      <input
                        value={faqQuery}
                        onChange={(e) => setFaqQuery(e.target.value)}
                        placeholder="جستجو در سوالات..."
                        className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pr-8 pl-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:py-2 sm:pr-9 sm:pl-3 sm:text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => setFaqCategory('all')}
                        className={`w-full rounded-lg border px-2.5 py-2 text-right text-xs transition-all duration-200 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm ${
                          faqCategory === 'all'
                            ? 'bg-[#1C3B9B] text-white border-[#1C3B9B] shadow-[0_8px_16px_rgba(28,59,155,0.2)]'
                            : 'bg-white text-[#334155] border-slate-200 hover:border-[#1C3B9B] hover:bg-[#F8FAFF]'
                        }`}
                      >
                        همه دسته‌ها
                      </button>
                      <button
                        onClick={() => setFaqCategory('learning')}
                        className={`w-full rounded-lg border px-2.5 py-2 text-right text-xs transition-all duration-200 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm ${
                          faqCategory === 'learning'
                            ? 'bg-[#1C3B9B] text-white border-[#1C3B9B] shadow-[0_8px_16px_rgba(28,59,155,0.2)]'
                            : 'bg-white text-[#334155] border-slate-200 hover:border-[#1C3B9B] hover:bg-[#F8FAFF]'
                        }`}
                      >
                        فرآیند یادگیری
                      </button>
                      <button
                        onClick={() => setFaqCategory('access')}
                        className={`w-full rounded-lg border px-2.5 py-2 text-right text-xs transition-all duration-200 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm ${
                          faqCategory === 'access'
                            ? 'bg-[#1C3B9B] text-white border-[#1C3B9B] shadow-[0_8px_16px_rgba(28,59,155,0.2)]'
                            : 'bg-white text-[#334155] border-slate-200 hover:border-[#1C3B9B] hover:bg-[#F8FAFF]'
                        }`}
                      >
                        دسترسی و محتوا
                      </button>
                      <button
                        onClick={() => setFaqCategory('payment')}
                        className={`w-full rounded-lg border px-2.5 py-2 text-right text-xs transition-all duration-200 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm ${
                          faqCategory === 'payment'
                            ? 'bg-[#1C3B9B] text-white border-[#1C3B9B] shadow-[0_8px_16px_rgba(28,59,155,0.2)]'
                            : 'bg-white text-[#334155] border-slate-200 hover:border-[#1C3B9B] hover:bg-[#F8FAFF]'
                        }`}
                      >
                        پرداخت و پشتیبانی
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setFaqCategory('all');
                        setFaqQuery('');
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold hover:border-[#1C3B9B] hover:bg-[#F8FAFF] sm:rounded-xl sm:px-3 sm:py-2 sm:text-xs"
                    >
                      پاکسازی فیلتر
                    </button>
                  </div>

                  <div className="space-y-2.5 sm:space-y-3">
                    {filteredFaqs.map((faq) => {
                      const isOpen = expandedFaqId === faq.id;
                      return (
                        <div
                          key={faq.id}
                          className={`overflow-hidden rounded-lg border bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)] transition-all duration-200 sm:rounded-xl ${
                            isOpen ? 'border-[#C9DAFF] shadow-[0_12px_28px_rgba(28,59,155,0.12)]' : 'border-[#DDE5FF] hover:border-[#C9DAFF]'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedFaqId(isOpen ? '' : faq.id)}
                            className={`course-interactive-btn w-full px-3 py-3 text-right transition-colors sm:px-4 sm:py-4 ${
                              isOpen ? 'bg-[#F8FAFF]' : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2 sm:gap-3">
                              <div>
                                <p className="text-sm font-bold leading-6 text-[#0F172A] sm:leading-7">{faq.question}</p>
                                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-[#CFE0FF] bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-extrabold text-[#1C3B9B] shadow-[0_4px_10px_rgba(28,59,155,0.12)] sm:mt-2 sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[11px]">
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#1C3B9B]" />
                                  {FAQ_TAB_LABELS[faq.category === 'payment' || faq.category === 'access' || faq.category === 'learning' ? faq.category : 'learning']}
                                </div>
                              </div>
                              <span className={`text-[#1C3B9B] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>⌄</span>
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3 sm:px-4 sm:pb-4">
                              <div className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3 sm:p-4">
                                <p className="text-sm leading-6 text-[#475569] sm:leading-7">{faq.answer}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {filteredFaqs.length === 0 && faqItems.length > 0 && (
                      <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-xs text-[#64748B] sm:rounded-xl sm:p-6 sm:text-sm">
                        سوالی با این فیلتر پیدا نشد.
                      </div>
                    )}
                  </div>
                </div>
                )}

                <div className="mt-4 rounded-lg border border-[#C11277]/20 bg-[#FDF0F7] p-3 shadow-[0_6px_18px_rgba(193,18,119,0.08)] sm:mt-5 sm:rounded-xl sm:p-4">
                  <p className="mb-2 text-xs font-semibold text-[#7A1C5A] sm:text-sm">سوالت اینجا نبود؟</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      value={faqQuestionDraft}
                      onChange={(e) => setFaqQuestionDraft(e.target.value)}
                      placeholder="سوالت را برای پشتیبانی بنویس..."
                      className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
                    />
                    <button
                      onClick={() => setFaqQuestionDraft('')}
                      className="shrink-0 rounded-lg bg-gradient-to-r from-[#1C3B9B] to-[#C11277] px-3 py-1.5 text-xs font-semibold text-white sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm"
                    >
                      ارسال سوال
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="order-1 hidden lg:order-2 lg:block">
            <div
              className="z-30 overflow-hidden rounded-[16px] border border-[#D6D9E0] bg-white p-3 shadow-[0_10px_28px_rgba(15,23,42,0.10)] sm:p-4 lg:sticky"
              style={{ top: `${asideStickyTop}px` }}
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-[10px] bg-slate-300">
                <img
                  src={imageSrc}
                  alt={course.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/assets/homeV2/01.jpg';
                  }}
                />
                {showIntroPlayback && (
                  <button
                    type="button"
                    onClick={() => setIsIntroVideoModalOpen(true)}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 transition hover:bg-black/30"
                    aria-label="پخش ویدیو معرفی"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md">
                      <FontAwesomeIcon icon={faPlay} />
                    </span>
                  </button>
                )}
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between">
                  {showPriceDiscount && (
                  <span className="rounded-md bg-[#DC2626] px-2 py-0.5 text-[11px] font-black text-white">
                    ٪{toFaDigits(discountPercentBadge)}
                  </span>
                  )}
                  <p className={`text-[15px] font-bold text-slate-700 ${showPriceDiscount ? '' : 'w-full text-center'}`}>هزینه آموزش</p>
                </div>
                {showPriceDiscount && (
                <div className="mt-1 flex items-center gap-2 text-left">
                  <p className="text-[18px] text-slate-400 line-through">{displayListPrice.toLocaleString('fa-IR')}</p>
                </div>
                )}
                {showPayableAsFree ? (
                  <div className="mt-1 flex justify-center">
                    <FreeBadge size="lg" />
                  </div>
                ) : (
                  <p className="mt-1 text-[34px] leading-none font-black text-[#0F172A]">
                    {payablePrice.toLocaleString('fa-IR')}
                    <span className="mr-2 text-[14px] font-bold text-slate-700">تومان</span>
                  </p>
                )}
              </div>

              {showDiscountCountdown && (
              <div className="mt-3 rounded-lg bg-[#FBE4EA] px-3 py-2 text-center text-[14px] font-extrabold text-[#B91C1C] leading-relaxed">
                {discountCountdownLabel}
              </div>
              )}

              {!isEnrolled ? (
                <div className="mt-3 flex items-center gap-2">
                  <button
                    className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#3A68C2] text-[#3A68C2] hover:bg-blue-50 transition"
                    aria-label="نشان کردن"
                  >
                    <FontAwesomeIcon icon={faBookmark} />
                  </button>
                  <button
                    onClick={handleAddToCart}
                    disabled={isCourseInCart}
                    className="flex-1 rounded-lg bg-[#3A68C2] px-4 py-3 text-base font-extrabold text-white hover:bg-[#345DAF] transition disabled:opacity-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FontAwesomeIcon icon={faCartShopping} />
                      {isCourseInCart ? 'در سبد خرید' : 'افزودن به سبد سفارش'}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-100 px-4 py-3 text-center font-semibold text-emerald-700">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-600 text-base" />
                    <span>شما در این دوره ثبت‌نام هستید</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleWatchCourse}
                    className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-l from-[#0EA5A5] to-[#0F766E] px-4 py-2.5 text-[14px] font-bold text-white shadow-[0_8px_18px_-6px_rgba(13,148,136,0.5)] transition-all duration-300 hover:brightness-[1.06] active:scale-[0.98]"
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                    />
                    <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                      <FontAwesomeIcon icon={faPlay} className="text-[9px] translate-x-[1px]" />
                    </span>
                    <span className="relative">تماشای دوره</span>
                  </button>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                    <div className="mb-1.5 flex items-center justify-between text-[12px]">
                      <span className="font-semibold text-slate-700">درصد تکمیل دوره</span>
                      <span className="font-black text-slate-900">{toFaDigits(courseProgressPercent)}٪</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-700"
                        style={{ width: `${courseProgressPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 border-t border-slate-200 pt-3">
                <ul className="space-y-2.5">
                  {courseAccessFeatures.map((item) => (
                    <li key={item.label} className="flex items-center justify-between gap-3 text-slate-600">
                      <span className="text-[15px] font-semibold leading-7 text-slate-700">{item.label}</span>
                      <FontAwesomeIcon icon={item.icon} className="shrink-0 text-[15px] text-slate-900" />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>

        {showSatisfactionSection && (
        <section className="-mx-4 mt-8 w-[calc(100%+2rem)] rounded-none border border-[#DDE5FF] bg-white/95 p-4 shadow-[0_12px_28px_rgba(15,23,42,0.07)] sm:mx-0 sm:w-auto sm:rounded-3xl sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 sm:mb-5 sm:gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                رضایت <span className="text-[#7C3AED]">کاربران</span> دوره
              </h2>
              <p className="mt-1 text-xs text-[#64748B] sm:text-sm">بازخورد واقعی شرکت‌کنندگان از تجربه این دوره</p>
            </div>
            <span className="hidden min-w-[80px] flex-1 self-center border-t border-slate-200/70 lg:block" />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => satisfactionCarouselRef.current?.scrollBy({ left: 220, behavior: 'smooth' })}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 sm:h-9 sm:w-9 sm:rounded-xl disabled:opacity-40"
                disabled={satisfactionStoriesResolved.length === 0}
                aria-label="اسلاید قبلی رضایت کاربران"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                type="button"
                onClick={() => satisfactionCarouselRef.current?.scrollBy({ left: -220, behavior: 'smooth' })}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 sm:h-9 sm:w-9 sm:rounded-xl disabled:opacity-40"
                disabled={satisfactionStoriesResolved.length === 0}
                aria-label="اسلاید بعدی رضایت کاربران"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {satisfactionAvgResolved ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-center sm:rounded-xl sm:px-3 sm:py-2">
                  <p className="text-xl font-black text-[#1C3B9B] sm:text-2xl">{toFaDigits(satisfactionAvgResolved)}</p>
                  <p className="text-[11px] text-[#64748B]">میانگین رضایت</p>
                </div>
              ) : null}
            </div>
          </div>

          {satisfactionKpisResolved.length > 0 ? (
          <div
            className={`mb-4 grid gap-2 sm:mb-5 sm:gap-2.5 grid-cols-2 ${
              satisfactionKpisResolved.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-2'
            }`}
          >
            {satisfactionKpisResolved.slice(0, 8).map((kpi) => (
              <div
                key={`${kpi.label}-${kpi.valueFa}`}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center sm:rounded-xl sm:px-3 sm:py-2"
              >
                <p className="text-base font-black text-[#0F172A] sm:text-lg">{toFaDigits(kpi.valueFa)}</p>
                <p className="text-[11px] text-[#64748B]">{kpi.label}</p>
              </div>
            ))}
          </div>
          ) : null}

          {satisfactionStoriesResolved.length > 0 ? (
          <div ref={satisfactionCarouselRef} className="course-hscroll no-scrollbar flex snap-x snap-mandatory items-start gap-2.5 overflow-x-auto pb-1 sm:gap-3.5">
            {satisfactionStoriesResolved.map((item) => {
              const coverSrc = getImageUrlWithFallback(item.cover, '/assets/homeV2/01.jpg');
              const embed = (item.videoEmbedUrl ?? '').trim();
              return (
              <article
                key={item.id}
                className="flex w-[148px] min-w-[148px] max-w-[148px] snap-start flex-col gap-2 sm:w-[176px] sm:min-w-[176px] sm:max-w-[176px] md:w-[188px] md:min-w-[188px] md:max-w-[188px]"
              >
                <div className="relative overflow-hidden rounded-[1.35rem] bg-gradient-to-br from-[#C11277] via-[#7C3AED] to-[#1C3B9B] p-[2.5px] shadow-[0_16px_32px_-20px_rgba(28,59,155,0.45)]">
                  <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[1.2rem] bg-slate-100">
                    <img
                      src={coverSrc}
                      alt={`کاور ویدیو رضایت ${item.name}`}
                      className="h-full w-full object-cover"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-2.5 pb-2.5 pt-12 sm:px-3 sm:pb-3 sm:pt-14">
                      <p className="truncate text-xs font-extrabold text-white sm:text-[13px]">{item.name}</p>
                      <p className="truncate text-[10px] text-white/85 sm:text-[11px]">{item.role}</p>
                      <span className="mt-1 block text-[10px] text-amber-400 sm:text-[11px]">
                        {'★'.repeat(item.score)}
                      </span>
                    </div>
                    {embed ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveSatisfactionVideoUrl(embed);
                          setActiveSatisfactionVideoTitle(`ویدیو رضایت ${item.name}`);
                          setIsSatisfactionVideoModalOpen(true);
                        }}
                        className="absolute left-1/2 top-[42%] inline-flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#1C3B9B] shadow-[0_10px_24px_-12px_rgba(15,23,42,0.55)] transition hover:scale-105 hover:bg-white sm:h-11 sm:w-11"
                        aria-label={`پخش ویدیو رضایت ${item.name}`}
                      >
                        <FontAwesomeIcon icon={faPlay} className="mr-[-1px] text-[11px] sm:text-xs" />
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="text-[11px] leading-5 text-[#475569] sm:text-xs sm:leading-6">
                  {item.comment}
                </p>
                {!embed ? (
                  <p className="text-[10px] text-slate-400">ویدیویی ثبت نشده</p>
                ) : null}
              </article>
            );})}
          </div>
          ) : null}
        </section>
        )}

        <section className="-mx-4 mt-6 w-[calc(100%+2rem)] rounded-none border border-[#DDE5FF] bg-white/95 p-4 shadow-[0_12px_28px_rgba(15,23,42,0.07)] sm:mx-0 sm:w-auto sm:rounded-3xl sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5 sm:mb-5 sm:gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-[#0F172A] sm:text-2xl">
                مقاله‌های <span className="text-[#7C3AED]">مرتبط</span> با دوره
              </h2>
              <p className="mt-1 text-xs text-[#64748B] sm:text-sm">مطالعه تکمیلی برای درک عمیق‌تر مفاهیم این مسیر آموزشی</p>
            </div>
            <span className="hidden min-w-[80px] flex-1 self-center border-t border-slate-200/70 lg:block" />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => articlesCarouselRef.current?.scrollBy({ left: 340, behavior: 'smooth' })}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 sm:h-9 sm:w-9 sm:rounded-xl"
                aria-label="اسلاید قبلی مقالات"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                onClick={() => articlesCarouselRef.current?.scrollBy({ left: -340, behavior: 'smooth' })}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 sm:h-9 sm:w-9 sm:rounded-xl"
                aria-label="اسلاید بعدی مقالات"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button className="rounded-lg border border-[#3A68C2] bg-[#3A68C2] px-2.5 py-1.5 text-[11px] font-bold text-white transition hover:bg-[#345DAF] sm:rounded-xl sm:px-3 sm:py-2 sm:text-xs">
                مشاهده همه مقالات
              </button>
            </div>
          </div>

          <div ref={articlesCarouselRef} className="course-hscroll no-scrollbar flex snap-x snap-mandatory items-stretch gap-2 overflow-x-auto pb-1 sm:gap-3">
            {relatedArticles.map((article) => (
              <article
                key={article.id}
                className="group min-w-[245px] snap-start overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C8D6FF] hover:shadow-[0_12px_22px_rgba(28,59,155,0.10)] sm:min-w-[320px] sm:rounded-2xl md:min-w-[340px]"
              >
                <div className="relative">
                  <img src={article.cover} alt={article.title} className="w-full aspect-[16/9] object-cover" />
                  <span className="absolute right-2 top-2 rounded-full border border-[#D6E2FF] bg-white/90 px-1.5 py-0.5 text-[9px] font-extrabold text-[#1C3B9B] backdrop-blur sm:px-2 sm:py-1 sm:text-[10px]">
                    {article.category}
                  </span>
                </div>
                <div className="p-2.5 sm:p-3">
                  <p className="min-h-[42px] line-clamp-2 text-sm font-extrabold leading-6 text-[#0F172A] sm:min-h-[48px]">{article.title}</p>
                  <p className="mt-1 min-h-[42px] line-clamp-2 text-[11px] leading-5 text-[#64748B] sm:min-h-[48px] sm:text-xs sm:leading-6">{article.excerpt}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-slate-500 sm:text-[11px]">زمان مطالعه: {article.readTime}</span>
                    <button className="text-[10px] font-extrabold text-[#1C3B9B] transition hover:text-[#16307F] sm:text-[11px]">
                      مطالعه مقاله
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
      {isShareModalOpen && (
        <div
          className="fixed inset-0 z-[125] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          onClick={() => setIsShareModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_70px_rgba(15,23,42,0.32)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-[linear-gradient(135deg,#F8FAFC_0%,#EEF2FF_100%)] px-4 py-3.5">
              <div>
                <p className="text-base font-black text-slate-900">اشتراک‌گذاری دوره</p>
                <p className="mt-0.5 text-[12px] font-semibold text-slate-600">رسانه موردنظر را انتخاب کنید</p>
              </div>
              <button
                onClick={() => setIsShareModalOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-100"
                aria-label="بستن پنجره اشتراک‌گذاری"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-[11px] font-extrabold text-slate-700">اشتراک مستقیم</span>
                <span className="text-[10px] font-bold text-slate-500">انتخاب پیام‌رسان</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {shareTargets.map((target) => (
                  <button
                    key={target.id}
                    type="button"
                    onClick={() => handleOpenShareTarget(target.url)}
                    className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_20px_rgba(15,23,42,0.10)]"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-[linear-gradient(145deg,#FFFFFF_0%,#F1F5F9_100%)] text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)] transition group-hover:scale-105">
                      <FontAwesomeIcon icon={target.icon} className="text-[14px]" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-[12px] font-black text-slate-800">{target.label}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <span className="text-[11px] font-semibold text-slate-600">اگر پیام‌رسان باز نشد، لینک را کپی کنید.</span>
                <button
                  type="button"
                  onClick={handleCopyCourseLink}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-slate-700 transition hover:bg-slate-100"
                >
                  <FontAwesomeIcon icon={faLink} className="text-[11px] text-slate-900" />
                  {shareCopied ? 'کپی شد' : 'کپی لینک'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isIntroVideoModalOpen && showIntroPlayback && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/65 p-4"
          onClick={() => setIsIntroVideoModalOpen(false)}
        >
          <div
            className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-[0_24px_48px_rgba(15,23,42,0.30)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-extrabold text-[#0F172A]">ویدیو معرفی دوره</p>
              <button
                type="button"
                onClick={() => setIsIntroVideoModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                aria-label="بستن پنجره ویدیو معرفی"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="aspect-video w-full bg-black">
              {heroIntroIframeSrc ? (
                <iframe
                  src={heroIntroIframeSrc}
                  title="ویدیو معرفی دوره"
                  className="h-full w-full"
                  allowFullScreen
                />
              ) : introStreamSrc ? (
                <CourseVideoPlayer src={introStreamSrc} poster={introVideoPoster} />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {lessonMediaModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/65 p-4"
          onClick={() => setLessonMediaModal(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-[0_24px_48px_rgba(15,23,42,0.30)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="lesson-media-modal-title"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <p id="lesson-media-modal-title" className="text-sm font-extrabold text-[#0F172A] line-clamp-2">
                {lessonMediaModal.title}
              </p>
              <button
                type="button"
                onClick={() => setLessonMediaModal(null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                aria-label="بستن پخش آپدیت"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {lessonMediaModal.variant === 'audio' ? (
              <div className="bg-slate-900 px-4 py-6">
                <CourseAudioPlayer
                  src={lessonMediaModal.src}
                  mediaKey={lessonMediaModal.src}
                  title={lessonMediaModal.title}
                  subtitle="فایل صوتی جلسه"
                  variant="compact"
                  showClose
                  onClose={() => setLessonMediaModal(null)}
                />
              </div>
            ) : (
              <div className="aspect-video w-full bg-black">
                {lessonMediaModal.variant === 'iframe' ? (
                  <iframe
                    src={lessonMediaModal.src}
                    title={lessonMediaModal.title}
                    className="h-full w-full"
                    allowFullScreen
                  />
                ) : (
                  <CourseVideoPlayer src={lessonMediaModal.src} />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {isSatisfactionVideoModalOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/65 p-4"
          onClick={() => setIsSatisfactionVideoModalOpen(false)}
        >
          <div
            className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-[0_24px_48px_rgba(15,23,42,0.30)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-extrabold text-[#0F172A]">{activeSatisfactionVideoTitle || 'ویدیو رضایت مشتری'}</p>
              <button
                onClick={() => setIsSatisfactionVideoModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                aria-label="بستن پنجره ویدیو رضایت مشتری"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="aspect-video bg-black">
              {activeSatisfactionVideoUrl?.trim() ? (
                <iframe
                  src={activeSatisfactionVideoUrl.trim()}
                  title={activeSatisfactionVideoTitle || 'ویدیو رضایت مشتری'}
                  className="h-full w-full"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-white/80">
                  آدرس ویدیو نامعتبر است.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isPracticePdfModalOpen && activePracticeSession?.pamphletUrl ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/65 p-4"
          onClick={() => setIsPracticePdfModalOpen(false)}
        >
          <div
            className="w-full max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-[0_24px_48px_rgba(15,23,42,0.30)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-extrabold text-[#0F172A]">جزوه PDF - {activePracticeSession.title}</p>
              <div className="flex items-center gap-2">
                <a
                  href={practicePdfBlobUrl || activePracticeSession.pamphletUrl}
                  download={`${activePracticeSession.title}.pdf`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700 transition hover:bg-emerald-100"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  دانلود جزوه
                </a>
                <a
                  href={activePracticeSession.pamphletUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-extrabold text-slate-700 transition hover:bg-slate-100"
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  باز کردن در تب جدید
                </a>
                <button
                  type="button"
                  onClick={() => setIsPracticePdfModalOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                  aria-label="بستن پنجره جزوه PDF"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-100">
              {practicePdfLoading ? (
                <div className="flex h-full items-center justify-center text-sm font-bold text-slate-500">
                  در حال بارگذاری جزوه…
                </div>
              ) : practicePdfFailed && !practicePdfBlobUrl ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                  <p className="text-sm font-bold text-slate-700">
                    نمایش جزوه در همین صفحه ممکن نشد.
                  </p>
                  <a
                    href={activePracticeSession.pamphletUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs font-extrabold text-red-700 transition hover:bg-red-100"
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    باز کردن جزوه در تب جدید
                  </a>
                </div>
              ) : (
                <iframe
                  src={practicePdfBlobUrl || activePracticeSession.pamphletUrl}
                  title={`PDF ${activePracticeSession.title}`}
                  className="h-full w-full"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
      {showCartToast ? (
      <div
        className="fixed inset-0 z-[130] flex items-center justify-center p-4"
        aria-live="polite"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className="absolute inset-0 bg-slate-950/35"
          aria-label="بستن"
          onClick={() => setShowCartToast(false)}
        />
        <div
          className="relative z-[1] w-[min(92vw,28rem)] rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_20px_56px_-24px_rgba(15,23,42,0.45)]"
        >
          <span className="pointer-events-none absolute -left-8 -top-8 h-24 w-24 rounded-full bg-[#1C3B9B]/10 blur-2xl" />
          <span className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full bg-[#C11277]/10 blur-2xl" />
          <div className="relative rounded-xl border border-white/70 bg-white/80 p-3 backdrop-blur">
            <div className="flex items-start gap-3">
              <div className="relative">
                <img
                  src={cartToastThumb}
                  alt="تصویر دوره اضافه شده به سبد خرید"
                  className="h-16 w-16 shrink-0 rounded-xl object-cover ring-1 ring-slate-200"
                />
                <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-emerald-500 px-1 text-[10px] font-black text-white shadow-sm">
                  ✓
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700">
                  اضافه شد به سبد
                </p>
                <p className="mt-1.5 line-clamp-2 text-[15px] font-black leading-6 text-[#0F172A]">{course.title}</p>
                <p className="mt-1 text-[11px] leading-5 text-slate-500">با خیال راحت می‌تونی ادامه بدی یا مستقیم بری سمت تسویه.</p>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCartToast(false)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    ادامه خرید
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/cart')}
                    className="rounded-lg bg-[#1C3B9B] px-3 py-1.5 text-[11px] font-extrabold text-white shadow-[0_10px_20px_-12px_rgba(28,59,155,0.9)] transition hover:bg-[#16307F]"
                  >
                    رفتن به سبد
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      ) : null}
      <style>{`
        .timer-shell {
          animation: timerShellBreath 5.2s ease-in-out infinite;
        }

        .timer-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 9px;
          border-radius: 9999px;
          border: 1px solid #d7e3ff;
          background: rgba(255, 255, 255, 0.8);
          color: #1e3a8a;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95);
        }

        .timer-digits-frame {
          padding: 3px 8px;
          border-radius: 8px;
          border: 1px solid #d8e3fb;
          background: rgba(255, 255, 255, 0.78);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95);
        }

        .timer-border-beam {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent 0%, #60a5fa 28%, #c11277 52%, #1c3b9b 76%, transparent 100%);
          opacity: 0.8;
          animation: timerBeamSlide 5.5s linear infinite;
          pointer-events: none;
        }

        .timer-dot {
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: radial-gradient(circle at 35% 35%, #93c5fd 0%, #1c3b9b 70%);
          box-shadow: 0 0 0 0 rgba(193, 18, 119, 0.35);
          animation: timerPulse 1.9s ease-out infinite;
        }

        .timer-glow {
          position: absolute;
          width: 120px;
          height: 120px;
          border-radius: 9999px;
          filter: blur(28px);
          opacity: 0.18;
          animation: timerFloat 6.2s ease-in-out infinite;
          pointer-events: none;
        }

        .timer-glow-blue {
          background: #1c3b9b;
          top: -60px;
          right: -40px;
        }

        .timer-glow-magenta {
          background: #c11277;
          bottom: -70px;
          left: -35px;
          animation-delay: -3.1s;
        }

        .timer-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            transparent 20%,
            rgba(255, 255, 255, 0.5) 35%,
            transparent 55%
          );
          transform: translateX(-140%);
          animation: timerShimmer 6.5s ease-in-out infinite;
          pointer-events: none;
        }

        .timer-digits {
          letter-spacing: 0.06em;
          animation: timerDigitsGlow 3.2s ease-in-out infinite;
          font-weight: 900;
        }

        .price-card-shine {
          position: relative;
          isolation: isolate;
          animation: priceCardBreath 4.8s ease-in-out infinite;
        }

        .price-card-shine::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(
            110deg,
            transparent 20%,
            rgba(255, 255, 255, 0.55) 46%,
            transparent 72%
          );
          transform: translateX(-150%);
          animation: priceCardShine 5.2s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }

        .tabs-dock-pop {
          animation: tabsDockPop 980ms cubic-bezier(0.18, 0.86, 0.22, 1);
          transform-origin: top right;
        }

        .tabs-dock-orbit {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: radial-gradient(circle at 30% 30%, #93c5fd 0%, #1c3b9b 72%);
          box-shadow:
            0 0 0 0 rgba(28, 59, 155, 0.28),
            0 0 18px rgba(28, 59, 155, 0.45);
          animation: tabsDockOrbitPath 980ms cubic-bezier(0.18, 0.86, 0.22, 1) forwards;
          pointer-events: none;
          z-index: 2;
        }

        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }

        @keyframes timerPulse {
          0% { box-shadow: 0 0 0 0 rgba(193, 18, 119, 0.35); }
          70% { box-shadow: 0 0 0 10px rgba(193, 18, 119, 0); }
          100% { box-shadow: 0 0 0 0 rgba(193, 18, 119, 0); }
        }

        @keyframes timerFloat {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-6px) translateX(6px); }
        }

        @keyframes timerShimmer {
          0%, 78% { transform: translateX(-140%); }
          100% { transform: translateX(140%); }
        }

        @keyframes timerBeamSlide {
          0% { transform: translateX(-26%); opacity: 0.35; }
          50% { transform: translateX(26%); opacity: 0.9; }
          100% { transform: translateX(-26%); opacity: 0.35; }
        }

        @keyframes timerDigitsGlow {
          0%, 100% { color: #0f172a; text-shadow: 0 0 0 rgba(28, 59, 155, 0); transform: translateY(0); }
          50% { color: #1c3b9b; text-shadow: 0 0 14px rgba(28, 59, 155, 0.22); transform: translateY(-1px); }
        }

        @keyframes timerShellBreath {
          0%, 100% { box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06), 0 10px 24px rgba(28, 59, 155, 0.08); }
          50% { box-shadow: 0 4px 10px rgba(15, 23, 42, 0.09), 0 14px 28px rgba(28, 59, 155, 0.16); }
        }

        @keyframes priceCardShine {
          0%, 75% { transform: translateX(-150%); }
          100% { transform: translateX(150%); }
        }

        @keyframes priceCardBreath {
          0%, 100% {
            box-shadow:
              0 10px 20px rgba(15,23,42,0.07),
              0 0 0 rgba(147,197,253,0);
          }
          50% {
            box-shadow:
              0 16px 28px rgba(15,23,42,0.12),
              0 0 24px rgba(147,197,253,0.2);
          }
        }

        @keyframes tabsDockPop {
          0% {
            opacity: 0;
            transform: translateX(-220px) translateY(-18px) scale(0.82) rotate(-5deg);
            filter: blur(3px);
          }
          40% {
            opacity: 0.92;
            transform: translateX(-90px) translateY(-6px) scale(0.9) rotate(-2deg);
            filter: blur(1px);
          }
          72% {
            opacity: 1;
            transform: translateX(8px) translateY(0px) scale(1.05) rotate(0deg);
            filter: blur(0);
          }
          88% {
            transform: translateX(-3px) translateY(0px) scale(0.98);
          }
          100% {
            opacity: 1;
            transform: translateX(0) translateY(0) scale(1);
            filter: blur(0);
          }
        }

        @keyframes tabsDockOrbitPath {
          0% {
            transform: translate(-210px, -26px) scale(0.75);
            opacity: 0;
            box-shadow:
              0 0 0 0 rgba(28, 59, 155, 0.22),
              0 0 8px rgba(28, 59, 155, 0.3);
          }
          35% {
            opacity: 1;
            transform: translate(-110px, -14px) scale(1.06);
          }
          68% {
            transform: translate(-34px, -5px) scale(0.94);
          }
          100% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
            box-shadow:
              0 0 0 14px rgba(28, 59, 155, 0),
              0 0 0 rgba(28, 59, 155, 0);
          }
        }

      `}</style>
    </div>
  );
};

export default CourseDetail;